/**
 * Hair Quiz — questionnaire, recommendation engine and results renderer.
 *
 * ── Why this file is written the way it is ──────────────────────────────────
 *
 * ES2019 ONLY. No optional chaining, no nullish coalescing, no object spread,
 * no async/await, no Array#flat. The August 11 2026 incident is the precedent:
 * a single `?.` in global.js failed at PARSE time on older Safari/iOS and
 * Android WebViews, which took out `fetchConfig` and with it Add to Cart and
 * month-1 gift injection. A parse error kills the whole file, so a syntax slip
 * here does not degrade the quiz — it deletes it, silently, for exactly the
 * older-device traffic that paid social sends. Keep it boring.
 *
 * ── How recommendations are produced ────────────────────────────────────────
 *
 * Answers are reduced to a small set of derived signals (persona, pattern,
 * phase, scalp flags, damage flags, texture, time budget). Products then score
 * themselves against those signals through rules that each carry the sentence
 * explaining the match. Nothing is recommended without a reason the shopper
 * can read, and no rule fires on an answer that was never given.
 *
 * Bloom is always the centre of the routine. Peripherals have to earn their
 * place: each one clears a score threshold or it does not render. A card that
 * cannot say why it is there is a card that should not be there.
 *
 * Contraindications are hard exclusions, not negative weights — the derma
 * stamp genuinely should not be recommended onto an irritated or sensitive
 * scalp, and no amount of countervailing signal should outvote that.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-hair-quiz]');

  /* Hosted in a panel over another page, rather than owning the page. Changes
     nothing about the questions or the scoring — only what happens at the end,
     where a page would redirect and a sheet has somewhere better to go. */
  var SHEET_MODE = !!(root && root.hasAttribute('data-hq-sheet'));
  if (!root) return;

  var configEl = root.querySelector('[data-hq-config]');
  var CONFIG = {};
  try {
    CONFIG = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  var CATALOG = CONFIG.catalog || {};
  var SETTINGS = CONFIG.settings || {};
  var GIFTS = CONFIG.gifts || [];
  var MONTH1_GIFTS = CONFIG.month1_gifts || [];

  /* ── the discount ladder ────────────────────────────────────────────────
     Everything the reward counter needs, resolved once. `earning` is a lookup
     rather than a list so a step can ask "do I pay?" without a scan, and the
     conditional steps simply never appear in it. */
  var DISCOUNT = CONFIG.discount || {};
  var EARNING = {};
  (function () {
    var raw = String(DISCOUNT.earning_steps || '').split(',');
    for (var i = 0; i < raw.length; i++) {
      var id = raw[i].replace(/^\s+|\s+$/g, '');
      if (id) EARNING[id] = true;
    }
  })();
  var EARNING_COUNT = (function () { var n = 0; for (var k in EARNING) if (EARNING.hasOwnProperty(k)) n++; return n; })();
  /* Derived, not configured. See the section comment: the email used to take
     whatever the questions left over, which on the 6-month ladder made one
     field worth 22% against nine questions worth 9% between them. Now the
     bonus is capped per plan and the questions split the remainder, so
     answering is what earns and the email is a top-up rather than the prize. */
  var PER_QUESTION = 0;
  /* The CTA badge and the reward flight, switched off rather than removed.
     Everything they need is still here — btnLabel still knows what each step is
     worth, flyReward and the .hq-coin/.hq-btn__earn rules are untouched — so
     bringing them back is this line, not an archaeology exercise. The meter
     still updates as answers land; it just changes without a coin crossing the
     screen to explain it. */
  var REWARD_FLIGHT = false;

  /* Same app as the funnel beacon in ab-track.js. */
  /* The app that records and answers unlocks.
  
     Host-aware so the flow can actually be exercised on a dev theme. The
     production app only accepts the live storefront's origin, so a quiz
     completed on `shopify theme dev` was posting into a 403 and the PDP was
     reading back 'locked' — correct behaviour, and untestable. Nothing here
     loosens production: the check is on the page's own hostname, so a real
     storefront can never be pointed at localhost. */
  var UNLOCK_HOST = /^(127\.0\.0\.1|localhost)$/.test(window.location.hostname)
    ? 'http://localhost:3000'
    : 'https://fleur-ab-testing.vercel.app';
  var UNLOCK_ENDPOINT = UNLOCK_HOST + '/api/quiz/unlock';
  var SUMMARY_ENDPOINT = UNLOCK_HOST + '/api/quiz/summary';
  var ASK_ENDPOINT = UNLOCK_HOST + '/api/quiz/ask';

  var PHONE_BONUS = Number(DISCOUNT.phone_bonus) || 0;
  var PHONE_CODE = String(DISCOUNT.phone_code || '');

  /* Which plan's number the counter runs to. The PDP flags pass ?target=17|24|31;
     the announcement bar and homepage pass nothing and get the ceiling they were
     promised. Clamped to the configured set so a hand-edited URL cannot put an
     invented number on screen — completion unlocks all three either way, so this
     only guards what is displayed. */
  function resolveTarget() {
    var targets = DISCOUNT.targets || {};
    var allowed = {};
    for (var k in targets) if (targets.hasOwnProperty(k)) allowed[String(Number(targets[k]))] = true;
    /* In a sheet the URL is the product page's, which carries no target of its
       own — so every quiz opened from a panel fell through to the ceiling and
       ran the six-month ladder no matter which plan she had selected. The host
       reads the selection and hands it over here. */
    var q = root ? root.getAttribute('data-hq-target-pct') : null;
    if (!q) {
      try { q = new URLSearchParams(window.location.search).get('target'); } catch (e) {}
    }
    if (q && allowed[String(Number(q))]) return Number(q);
    return Number(DISCOUNT.default_target) || 0;
  }
  /* Everything the chosen plan decides, in one place and re-runnable.

     These were computed once at boot, which was right while the quiz was a
     page: the plan arrived in the URL and could not change without a reload.
     In a panel she can close it, pick a different plan on the page behind, and
     open it again on the same question — and every one of these figures is
     wrong for the plan she is now looking at. So they are assigned by a
     function rather than by an initialiser, and the function runs again
     whenever the host says the plan moved. */
  var TARGET = 0;
  var TARGET_MONTHS = null;
  var EMAIL_BONUS = 0;
  var QUESTION_POOL = 0;
  var TOTAL = 0;

  function resolvePlan() {
    TARGET = resolveTarget();

    /* Which plan this target belongs to, so the right bonus applies. */
    TARGET_MONTHS = null;
    var targets = DISCOUNT.targets || {};
    for (var k in targets) {
      if (targets.hasOwnProperty(k) && Number(targets[k]) === TARGET) { TARGET_MONTHS = k; break; }
    }

    var map = DISCOUNT.email_bonus_by_months || {};
    var v = TARGET_MONTHS !== null ? Number(map[TARGET_MONTHS]) : NaN;
    /* Clamped to the target: a bonus larger than the whole discount would
       leave the questions paying a negative amount. */
    if (!isFinite(v) || v < 0) v = 0;
    EMAIL_BONUS = Math.min(v, TARGET);

    QUESTION_POOL = Math.max(0, TARGET - EMAIL_BONUS);
    PER_QUESTION = EARNING_COUNT > 0 ? QUESTION_POOL / EARNING_COUNT : 0;

    /* The number the quiz advertises: everything on offer, phone bonus
       included. TARGET is what the questions and the email can reach on their
       own; TOTAL is what the shopper is actually being shown at the door, so it
       is what the headline claims and what the meter's far end is set to. The
       gap between them is the SMS step, and leaving it visible is the point —
       the bar cannot fill without it.

       Read from settings, not summed. The ladder is taken off the full price
       and the bonus code is then taken off what the ladder left, so the two do
       not add: 17 and 5 make 21.4% of the full price, not 22%, and the
       discrepancy grows with the ladder — 31 and 5 make 34.5%, not 36%. The
       settings carry the charged figures, rounded down, so the quiz promises a
       little less than it delivers rather than a little more.

       The sum is still the fallback, because a missing setting must not leave
       the meter with no far end at all — a quiz that cannot state a target is
       worse than one that overstates it by a point. */
    var totals = DISCOUNT.totals || {};
    var t = TARGET_MONTHS !== null ? Number(totals[TARGET_MONTHS]) : NaN;
    if (isFinite(t) && t > 0) TOTAL = PHONE_CODE ? t : TARGET;
    else TOTAL = TARGET + (PHONE_CODE ? PHONE_BONUS : 0);
  }

  resolvePlan();

  /* Reopened on a different plan.

     She closes the sheet on question four, picks the three-month card, and
     opens it again. Her answers are still hers — the questions have not
     changed and neither has what she said — but every figure on the meter
     belongs to the plan she left. So the ladder is re-resolved and the wheel
     rebuilt around her existing progress, which is the one thing that does
     carry across. */
  document.addEventListener('hq:plan', function () {
    var before = TARGET;
    resolvePlan();
    if (TARGET === before) return;

    /* The floor is a number of points on the old ladder and means nothing on
       the new one. Her progress through the questions is untouched, so it is
       rebuilt from that rather than carried over. */
    earnedFloor = 0;
    shownPct = earnedPercent();
    priceShown = null;
    reelPrices = null;
    paintReel(false);
    var step = STEPS[state.index];
    if (step) paintMeter(step);
  });

  /* The plans, and which one they came in on.

     The entry target names a plan: 17 is the 1-month, 24 the 3-month, 31 the
     6-month. Results shows that one alone — a visitor who clicked "unlock 24%"
     is shopping a 3-month, and putting three plans in front of them at the
     moment they have finished earning is a new decision where they wanted a
     conclusion. The other two are still unlocked; they are simply not the
     pitch here. */
  var PLANS = CONFIG.plans || [];

  function chosenPlan() {
    var targets = DISCOUNT.targets || {};
    var months = null;
    for (var k in targets) {
      if (targets.hasOwnProperty(k) && Number(targets[k]) === TARGET) { months = Number(k); break; }
    }
    for (var i = 0; i < PLANS.length; i++) {
      if (Number(PLANS[i].months) === months) return PLANS[i];
    }
    /* No match — a target with no plan behind it. Fall back to the longest
       plan rather than to nothing, so the page still has something to sell. */
    return PLANS.length ? PLANS[PLANS.length - 1] : null;
  }
  var STORAGE_KEY = 'fleur-hair-quiz-v1';

  /* =========================================================================
     Questions
     -------------------------------------------------------------------------
     `kind` is one of: intro | single | multi | interstitial | email |
     analysing | results.

     `showIf` gates a step on prior answers. `maxPick` caps a multi-select so
     the signal stays sharp — a shopper who ticks everything tells us nothing.
     `exclusive` marks an option that clears the rest ("none of these").
     ========================================================================= */

  var STEPS = [
    { id: 'intro', kind: 'intro' },

    {
      id: 'concern',
      kind: 'multi',
      maxPick: 3,
      title: 'what brought you here?',
      sub: 'Pick up to three.',
      options: [
        { id: 'thinning', label: 'My hair is thinning' },
        { id: 'shedding', label: 'I\u2019m shedding more than usual' },
        { id: 'breakage', label: 'It breaks and feels dry' },
        { id: 'flat', label: 'It\u2019s flat and lifeless' },
        { id: 'scalp', label: 'My scalp is itchy or flaky' },
        { id: 'edges', label: 'My hairline is receding' }
      ]
    },

    {
      id: 'pattern',
      kind: 'single',
      title: 'where do you notice it most?',
      showIf: function (a) {
        return has(a.concern, ['thinning', 'shedding', 'edges']);
      },
      options: [
        { id: 'part', label: 'A widening part' },
        { id: 'crown', label: 'The crown' },
        { id: 'temples', label: 'Temples and hairline' },
        { id: 'diffuse', label: 'Evenly, all over' },
        { id: 'patches', label: 'Distinct patches' }
      ]
    },

    {
      id: 'duration',
      kind: 'single',
      title: 'how long has this been going on?',
      options: [
        { id: 'acute', label: 'Under 3 months' },
        { id: 'subacute', label: '3 to 12 months' },
        { id: 'chronic', label: '1 to 3 years' },
        { id: 'longstanding', label: 'Over 3 years' }
      ]
    },

    {
      id: 'lifestage',
      kind: 'multi',
      title: 'has anything changed lately?',
      sub: 'Select all that apply.',
      options: [
        { id: 'postpartum', label: 'I had a baby' },
        { id: 'menopause', label: 'Perimenopause or menopause' },
        { id: 'stress', label: 'High stress, illness or surgery' },
        { id: 'diet', label: 'A big change in diet or weight' },
        { id: 'medical', label: 'A medication or medical condition' },
        { id: 'family', label: 'Thinning runs in my family' },
        { id: 'none', label: 'None of these', exclusive: true }
      ]
    },

    {
      id: 'postpartum_when',
      kind: 'single',
      title: 'how long ago?',
      showIf: function (a) {
        return has(a.lifestage, ['postpartum']);
      },
      options: [
        { id: 'pp_0_3', label: '0\u20133 months' },
        { id: 'pp_3_6', label: '3\u20136 months' },
        { id: 'pp_6_12', label: '6\u201312 months' },
        { id: 'pp_12_plus', label: 'Over a year' }
      ]
    },

    {
      id: 'menopause_stage',
      kind: 'single',
      title: 'which stage?',
      showIf: function (a) {
        return has(a.lifestage, ['menopause']);
      },
      options: [
        { id: 'peri', label: 'Perimenopause' },
        { id: 'meno', label: 'Menopause' },
        { id: 'post', label: 'Post-menopause' }
      ]
    },

    
    {
      id: 'hairtype',
      kind: 'single',
      title: 'how would you describe your hair?',
      grid: true,
      options: [
        { id: 'straight', label: 'Straight' },
        { id: 'wavy', label: 'Wavy' },
        { id: 'curly', label: 'Curly' },
        { id: 'coily', label: 'Coily' }
      ]
    },

    {
      id: 'texture',
      kind: 'single',
      title: 'and each strand is\u2026',
      grid: true,
      options: [
        { id: 'fine', label: 'Fine' },
        { id: 'medium', label: 'Medium' },
        { id: 'coarse', label: 'Coarse' },
        { id: 'unsure', label: 'Not sure' }
      ]
    },

    {
      id: 'scalp',
      kind: 'multi',
      title: 'what\u2019s your scalp like?',
      sub: 'Select all that apply.',
      options: [
        { id: 'oily', label: 'Oily, or product builds up' },
        { id: 'dry', label: 'Dry or tight' },
        { id: 'flaky', label: 'Flaky or itchy' },
        { id: 'sensitive', label: 'Sensitive' },
        { id: 'balanced', label: 'Comfortable', exclusive: true }
      ]
    },

    {
      id: 'damage',
      kind: 'multi',
      title: 'any of these in your routine?',
      sub: 'Select all that apply.',
      options: [
        { id: 'heat', label: 'Heat styling' },
        { id: 'color', label: 'Colour, highlights or bleach' },
        { id: 'chemical', label: 'Relaxer, perm or keratin' },
        { id: 'tension', label: 'Tight styles or extensions' },
        { id: 'none', label: 'None of these', exclusive: true }
      ]
    },

    {
      id: 'treatments',
      kind: 'single',
      title: 'what have you tried?',
      options: [
        { id: 'nothing', label: 'Nothing yet' },
        { id: 'otc', label: 'Shampoos or supplements' },
        { id: 'rx', label: 'Prescription treatment' },
        { id: 'lots', label: 'Most things \u2014 nothing stuck' }
      ]
    },

    {
      id: 'commitment',
      kind: 'single',
      title: 'how much time do you have?',
      options: [
        { id: 'minimal', label: 'Under a minute a day' },
        { id: 'moderate', label: 'A few minutes a day' },
        { id: 'committed', label: 'Whatever works' }
      ]
    },

    
    {
      id: 'email',
      kind: 'email',
      /* Not "your routine" — this quiz stopped rendering one, and asking for an
         address to send something that never arrives is a promise broken one
         screen later. The address is where the discount goes. */
      title: 'where should we send your discount?',
      sub: 'So it is waiting for you whenever you are ready to use it.'
    },

    {
      id: 'phone',
      kind: 'phone',
      /* No code configured means no offer to make, so the step disappears
         rather than promising a bonus nothing can hand over. */
      showIf: function () { return !!PHONE_CODE; },

      /* One number on the screen, and it is the one being offered.

         This previously stated the running total and the new total as well —
         "5% on top of your 31% — now 36% off" — on a page that already carries
         the tally and the button badge. Four figures competing to be the point
         is not a stronger offer than one, it is an arithmetic problem, and the
         ask here is a phone number, not a calculation.

         "on your first order" stays, and is doing compliance work rather than
         hedging: the earned discount is a selling plan and recurs for as long
         as they subscribe, while this bonus is a code that applies once. */
      title: 'get a bonus ' + wholePct(PHONE_BONUS) + '% discount',
      sub: 'On your first order. We apply it at checkout.'
    },

    { id: 'analysing', kind: 'analysing' },
    { id: 'results', kind: 'results' }
  ];

  /* =========================================================================
     Products and their scoring rules
     -------------------------------------------------------------------------
     `key` maps to CATALOG, which Liquid populates from live Shopify data, so
     prices, images and variant ids are never hard-coded here.

     Each rule is { when: fn(signals) -> bool, score: n, because: string }.
     The highest-scoring matched rule supplies the card's reason, so the copy
     the shopper reads is always the strongest true thing we can say.

     `veto` rules exclude the product outright regardless of score.
     ========================================================================= */

  var PRODUCTS = [
    {
      key: 'serum',
      hero: true,
      flag: 'The centre of your routine',
      fallback:
        'Six copper peptides, applied to the scalp daily. Everything else in your routine exists to get more of this where it needs to go.',
      rules: [
        {
          when: function (s) { return s.persona === 'postpartum'; },
          score: 10,
          because:
            'Postpartum shedding happens when a wave of follicles leaves their growth phase at once. Bloom supports the scalp through that reset so regrowth comes back in denser.'
        },
        {
          when: function (s) { return s.persona === 'menopause'; },
          score: 10,
          because:
            'As oestrogen falls, hairs spend less time growing before they shed. Bloom’s copper peptides support the scalp environment that longer growth phases depend on.'
        },
        {
          when: function (s) { return s.pattern === 'part' || s.pattern === 'crown'; },
          score: 9,
          because:
            'A widening part is the classic early signal of miniaturising follicles at the crown. Bloom is formulated for exactly this — applied daily, directly to the part line.'
        },
        {
          when: function (s) { return s.pattern === 'temples' || hasGoal(s, 'edges'); },
          score: 9,
          because:
            'Hairline and temple follicles are the smallest and the first to struggle. Bloom goes on those edges daily, where lighter products never make contact.'
        },
        {
          when: function (s) { return s.phase === 'acute' || s.phase === 'subacute'; },
          score: 8,
          because:
            'You’re early enough that this is worth taking seriously now. Follicles respond far better before miniaturisation sets in.'
        }
      ]
    },

    {
      key: 'derma_stamp',
      /* Never onto a compromised scalp barrier. This is the one hard line in
         the engine: a micro-needling tool on inflamed, flaking or reactive
         skin makes things worse, and no combination of other signals should
         be able to outweigh that. */
      veto: function (s) {
        return (
          s.scalp.sensitive || s.scalp.flaky || s.scalp.itchy || hasConcern(s, 'scalp')
        );
      },
      fallback:
        'Stamp once a week before Bloom. The micro-channels let far more of the serum reach the follicle instead of sitting on the surface.',
      rules: [
        {
          when: function (s) { return s.commitment === 'committed' && (hasGoal(s, 'density') || hasConcern(s, 'thinning')); },
          score: 9,
          because:
            'You said you’d do whatever works — this is that. One weekly pass before Bloom opens micro-channels so the peptides reach the follicle rather than the surface.'
        },
        {
          when: function (s) { return s.pattern === 'part' || s.pattern === 'crown' || s.pattern === 'temples'; },
          score: 8,
          because:
            'Targeted thinning is where the stamp earns its place — you can concentrate it exactly on the part, crown or temples once a week before applying Bloom.'
        },
        {
          when: function (s) { return s.phase === 'chronic' || s.phase === 'longstanding'; },
          score: 7,
          because:
            'After a year or more, follicles need more than a topical alone. Stamping weekly before Bloom drives significantly more product past the surface.'
        },
        {
          when: function (s) { return hasConcern(s, 'thinning') || hasGoal(s, 'density'); },
          score: 6,
          because:
            'Density is the goal that most rewards better absorption. One weekly pass before Bloom is the difference between product on your scalp and product in it.'
        }
      ]
    },

    {
      key: 'roller_ball',
      fallback:
        'Screws straight onto your Bloom bottle. Rolls the serum along the scalp in about twenty seconds, with none of it on your hands.',
      rules: [
        {
          when: function (s) { return s.commitment === 'minimal'; },
          score: 9,
          because:
            'You told us it has to be effortless. This is the single change that makes daily application take twenty seconds instead of two minutes — and consistency is most of the result.'
        },
        {
          when: function (s) { return s.hairtype === 'curly' || s.hairtype === 'coily'; },
          score: 8,
          because:
            'Getting serum to the scalp through curls and coils is the hard part. The roller parts the hair and delivers along the scalp line without disturbing your style.'
        },
        {
          when: function (s) { return s.texture === 'coarse' || s.density === 'dense'; },
          score: 7,
          because:
            'Dense, coarse hair soaks up a dropper before it ever reaches skin. The roller ball puts the serum on the scalp itself, where it does the work.'
        },
        {
          when: function (s) { return s.hairtype === 'straight' || s.hairtype === 'wavy'; },
          score: 5,
          because:
            'Cleaner and faster than a dropper — even application along the part with nothing running down your forehead or onto your hands.'
        }
      ]
    },

    {
      key: 'scalp_massager',
      fallback:
        'Sixty seconds of massage while Bloom is on. Stimulates the scalp and works the serum in past the surface.',
      rules: [
        {
          when: function (s) { return s.scalp.oily || s.scalp.buildup; },
          score: 9,
          because:
            'Buildup is the quiet reason serums underperform — it sits between the product and the skin. This lifts it at every wash and works Bloom in properly between them.'
        },
        {
          when: function (s) { return s.scalp.flaky || s.scalp.itchy || s.scalp.dry; },
          score: 8,
          because:
            'A gentle massage loosens flakes and relieves tightness without the scratching that keeps an irritated scalp irritated.'
        },
        {
          when: function (s) { return hasGoal(s, 'scalp') || hasConcern(s, 'scalp'); },
          score: 8,
          because:
            'You named the scalp as the problem. A minute of daily massage is the lowest-effort thing that directly addresses it — and it doubles as your Bloom application.'
        },
        {
          when: function (s) { return hasGoal(s, 'density') || s.damage.tension; },
          score: 6,
          because:
            'Massage supports circulation at the follicle and releases the tension that tight styling leaves in the scalp.'
        }
      ]
    },

    {
      key: 'detangle_comb',
      fallback:
        'Wide-tooth, seamless teeth that glide through knots instead of snapping them. Use on damp hair, from the ends up.',
      rules: [
        {
          when: function (s) { return hasConcern(s, 'breakage') || hasGoal(s, 'strength'); },
          score: 9,
          because:
            'A lot of what looks like shedding is actually breakage at the comb. Wide, seamless teeth work through a knot rather than tearing it — the fastest win on this list.'
        },
        {
          when: function (s) { return s.hairtype === 'curly' || s.hairtype === 'coily'; },
          score: 8,
          because:
            'Curls and coils tangle at every bend. Detangling damp, from the ends up, with wide teeth is the difference between keeping length and losing it.'
        },
        {
          when: function (s) { return s.damage.bleach || s.damage.chemical || s.damage.color || s.damage.heat; },
          score: 8,
          because:
            'Chemically or heat-treated hair has a compromised cuticle and snaps under a fine-tooth comb. This one distributes the load instead of concentrating it.'
        },
        {
          when: function (s) { return s.texture === 'fine' || hasGoal(s, 'length'); },
          score: 7,
          because:
            'Hair that won’t gain length is usually breaking at the ends as fast as it grows at the root. Gentler detangling is how you keep what you grow.'
        }
      ]
    },

    {
      key: 'satin_pillow',
      fallback:
        'Eight hours a night of cotton friction is the biggest unmanaged source of breakage. Satin lets hair slide instead of gripping it.',
      rules: [
        {
          when: function (s) { return hasConcern(s, 'breakage') || s.texture === 'fine'; },
          score: 8,
          because:
            'Fine and fragile hair takes the worst of overnight friction. Cotton grips and snaps as you move; satin lets it slide — and you do nothing to get the benefit.'
        },
        {
          when: function (s) { return s.hairtype === 'curly' || s.hairtype === 'coily'; },
          score: 8,
          because:
            'Cotton pulls moisture out of curls and roughs up the cuticle overnight. Satin preserves definition and cuts the morning detangle down to almost nothing.'
        },
        {
          when: function (s) { return s.damage.tension || s.damage.extensions || s.pattern === 'temples'; },
          score: 7,
          because:
            'Edges and hairline take friction from every turn of your head. Satin removes that load from the most fragile hair you have.'
        },
        {
          when: function (s) { return hasConcern(s, 'breakage') || hasConcern(s, 'flat'); },
          score: 6,
          because:
            'Satin doesn’t wick moisture the way cotton does, so hair keeps its own oils overnight — smoother, shinier, less frizz by morning.'
        }
      ]
    },

    {
      key: 'hair_guide',
      fallback:
        'The protocol that sequences everything above — what to do daily, what to do weekly, and what to stop doing.',
      rules: [
        {
          when: function (s) { return s.treatments === 'nothing'; },
          score: 9,
          because:
            'This is your first step, so start with the order of operations. The guide sequences the whole routine — what to do daily, weekly, and what to stop doing entirely.'
        },
        {
          when: function (s) { return s.treatments === 'lots'; },
          score: 8,
          because:
            'You’ve tried plenty and nothing stuck. Usually that’s a sequencing problem, not a product problem — this lays out the order that makes each piece work.'
        },
        {
          when: function (s) { return s.phase === 'acute' || s.persona === 'postpartum'; },
          score: 7,
          because:
            'Early on, what you stop doing matters as much as what you start. The guide covers both, with a month-by-month plan for the phase you’re in.'
        },
        {
          when: function (s) { return s.commitment === 'minimal' || s.treatments === 'otc'; },
          score: 5,
          because:
            'A short, practical protocol so you’re not guessing — the minimum effective routine, and the habits that quietly undo it.'
        }
      ]
    }
  ];

  /* Peripherals below this score are not shown. Set so that a product needs a
     real, specific signal behind it rather than a generic one. */
  var SCORE_FLOOR = 5;

  /* =========================================================================
     Small helpers (kept ES5-shaped on purpose)
     ========================================================================= */

  function has(arr, ids) {
    if (!arr || !arr.length) return false;
    for (var i = 0; i < ids.length; i++) {
      if (arr.indexOf(ids[i]) !== -1) return true;
    }
    return false;
  }

  function hasConcern(s, id) {
    return s.concerns.indexOf(id) !== -1;
  }

  function hasGoal(s, id) {
    return s.goals.indexOf(id) !== -1;
  }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function money(cents) {
    var n = Number(cents) / 100;
    var s = n.toFixed(2);
    if (s.indexOf('.00', s.length - 3) !== -1) s = s.slice(0, -3);
    return '$' + s;
  }

  function get(obj, key, fallback) {
    if (obj && Object.prototype.hasOwnProperty.call(obj, key) && obj[key] != null && obj[key] !== '') {
      return obj[key];
    }
    return fallback;
  }

  var CHECK =
    '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8.5L6.2 11.7L13 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* =========================================================================
     State
     ========================================================================= */

  var state = {
    index: 0,
    answers: {},
    email: '',
    phone: '',
    phoneBonus: 0,
    added: {}
  };

  /* Pending single-select auto-advance. Held at module scope so any step
     change can cancel it — otherwise tapping Back within the delay would be
     undone by the timer firing a moment later. */
  var advanceTimer = null;

  /* Mirrors the storefront cart's item_count, so the header badge reflects a
     cart the shopper may already have had before the quiz. */
  var cartCount = 0;

  /* The unlock, in a place the PDP can read it.

     Liquid cannot see this — it is a browser cookie, same as the A/B bucket —
     so the product page ships both ladders and the client hides one. First
     party and long lived so a shopper who comes back next week still has what
     they earned; cross-device is the known gap, and the recovery path is the
     email we just captured.

     Display state only. It decides which price is *shown*; which price is
     *charged* is decided by the selling plan the form submits, which is why a
     hand-set cookie cannot buy anything at a discount. */
  /**
   * Tell the app the quiz was finished.
   *
   * The cookie beside this is a hint for fast rendering; this is the record the
   * PDP actually asks about before it shows or charges a discounted price. It
   * has to be a record rather than a cookie because in that arm the unlock
   * chooses which selling plan the form submits, and a cookie is one line in a
   * console.
   *
   * Keyed on the funnel's anonymous visitor id, so no new identifier is
   * introduced and a completion lines up with the same shopper's exposures.
   * Silent on failure in both directions: the quiz has already delivered what
   * it promised on screen, and a shopper must never be held up by our
   * bookkeeping. A dropped call costs them the PDP discount until they retake
   * it, which is the safe way round.
   */
  /* ── entering and leaving the quiz ─────────────────────────────────────
     Everything recorded until now was a completion, so every share computed
     from it was conditioned on surviving nine questions. If one group quits
     earlier than another, its presence in the data is understated and its
     conversion is measured on whoever stayed — and nothing could tell the two
     apart.

     quiz_view is the denominator, quiz_step is where she got to, quiz_done is
     the end. Exit is the max step of an attempt that has no quiz_done: a
     leaving browser sends nothing, so the last thing reported is the answer.

     One attempt per run, so a retake is a second funnel and a later success
     does not erase the abandon before it — which is the population being
     counted. */
  var QUIZ_ATTEMPT_KEY = '_fleur_quiz_run';

  function quizAttempt() {
    try {
      var v = window.sessionStorage.getItem(QUIZ_ATTEMPT_KEY);
      if (v) return v;
      v = Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      window.sessionStorage.setItem(QUIZ_ATTEMPT_KEY, v);
      return v;
    } catch (e) {
      if (!quizAttempt._v) {
        quizAttempt._v = Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      }
      return quizAttempt._v;
    }
  }

  function newQuizAttempt() {
    try { window.sessionStorage.removeItem(QUIZ_ATTEMPT_KEY); } catch (e) {}
    quizAttempt._v = null;
  }

  var lastQuizStep = null;
  var quizRunDone = false;

  function quizEvent(event, step, idx) {
    try {
      if (typeof window.__abEvent !== 'function') return;
      window.__abEvent(event, { step: step, idx: idx, attempt: quizAttempt() });
    } catch (e) {}
  }

  function reportStep(step, index) {
    if (!step) return;

    /* Back at the intro after finishing is a second run, and it is the only
       signal that means that which cannot arrive before the first one ends. */
    if (step.kind === 'intro' && quizRunDone) {
      newQuizAttempt();
      quizRunDone = false;
      lastQuizStep = null;
    }

    /* Re-renders, resizes and repaints all land here. Reporting them would
       inflate every count with the same visitor standing still. */
    var key = step.id + ':' + index;
    if (key === lastQuizStep) return;
    lastQuizStep = key;

    if (step.kind === 'results') {
      quizRunDone = true;
      quizEvent('quiz_done', step.id, index);
      return;
    }
    quizEvent('quiz_step', step.id, index);
  }

  function recordUnlock() {
    var vid = null;
    try {
      var m = document.cookie.match('(^|;)\\s*_ab_vid\\s*=\\s*([^;]+)');
      vid = m ? m.pop() : null;
    } catch (e) {}
    if (!vid) return;

    var payload = {
      visitorId: vid,
      target: Math.round(TARGET),
      bonus: state.phone && PHONE_CODE ? Math.round(PHONE_BONUS) : 0,
      email: state.email || null,
      /* Sent so consent can be recorded server-side. The browser's identify
         call puts the number on the profile but cannot consent it, and an
         unconsented number is one Klaviyo will not text — which is the entire
         point of asking for it. */
      phone: state.phone || null,
      /* The answers themselves. They already reach Klaviyo as profile
         properties, which is what makes them usable for audiences; this is so
         they can be aggregated and joined to orders, which profile properties
         cannot do. */
      answers: state.answers || null,
      persona: (function () {
        try {
          return buildSignals().persona || null;
        } catch (e) {
          return null;
        }
      })()
    };

    try {
      window.fetch(UNLOCK_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      })['catch'](function () {});
    } catch (e) {}
  }

  /* =========================================================================
     Results summary
     -------------------------------------------------------------------------
     The paragraph above the offer. Written by a model server-side, with the
     hand-written per-persona copy below as the fallback.

     Why it came back: the deal on the entry point is "answer these, get the
     discount", and that framing is honest — but a thirteen-question form that
     asks about menopause stage and medications sets an expectation the button
     label does not undo. The segment that pays for the mismatch is the one
     whose barrier was never price. Menopause is 40% of takers, reports
     "moderate" commitment at twice everyone else's rate, and converts at half
     the stress-shed segment's. A discount does not answer "is this even
     fixable", which is the question those answers were asking.

     Three rules it lives by, all of them about not becoming the friction it
     replaced:

       - It never delays the discount. The request goes out when the analysing
         screen appears and gets the 2.9 seconds that screen was already
         spending. Whatever has not landed by then is not waited for — which,
         for a cold cell, is everything: generation measures 8-26s. Generated
         copy reaches a shopper via the cache or not at all, so the cells are
         warmed ahead of deploy rather than filled by the first visitor to
         each one.
       - It never swaps under the shopper. The choice between generated copy
         and the fallback is made once, on the first results render, and held.
         Text that rewrites itself mid-read is worse than either version.
       - It is never the reason a shopper is stuck. Every failure path lands on
         the fallback, which is a good paragraph rather than an empty one.
     ========================================================================= */

  var summary = {
    status: 'idle', teaser: null, lead: null, points: [], bridge: '',
    specifics: '', timeline: '', questions: [], shown: null
  };

  /**
   * Arm B only, by instruction.
   *
   * In arm A the quiz is promoted on the home page and the PDP prices are
   * already discounted, so the results screen is a small and differently-
   * motivated path — 10 takers against arm B's 56 over the current run. This
   * is a change to arm B's treatment and belongs to arm B alone.
   *
   * `window.__ab` is set inline in <head> by snippets/ab-assign.liquid, so it
   * is present well before the quiz mounts. Absent means no test is running
   * on this page, which is a no.
   */
  function inTreatmentArm() {
    try {
      return !!(window.__ab && window.__ab.bucket === 'b');
    } catch (e) {
      return false;
    }
  }

  function requestSummary() {
    if (summary.status !== 'idle') return;
    if (!inTreatmentArm()) {
      summary.status = 'off';
      return;
    }

    var persona = null;
    try {
      persona = buildSignals().persona || null;
    } catch (e) {}

    summary.status = 'pending';

    try {
      window
        .fetch(SUMMARY_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers: state.answers || null, persona: persona })
        })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (body) {
          var s = body && body.summary;
          if (s && s.teaser && s.lead) {
            summary.teaser = String(s.teaser);
            summary.lead = String(s.lead);
            summary.points = Array.isArray(s.points) ? s.points : [];
            summary.bridge = s.bridge ? String(s.bridge) : '';
            summary.specifics = s.specifics ? String(s.specifics) : '';
            summary.timeline = s.timeline ? String(s.timeline) : '';
            summary.questions = Array.isArray(s.questions) ? s.questions : [];
            summary.status = 'ready';
          } else {
            summary.status = 'failed';
          }
        })
        ['catch'](function () {
          summary.status = 'failed';
        });
    } catch (e) {
      summary.status = 'failed';
    }
  }

  /**
   * The fallback, assembled from buildDiagnosis.
   *
   * Its `lead` opens with the mechanism, so the first sentence is a genuine
   * teaser rather than a preamble — cutting there gives the same shape the
   * generated copy is asked for, and the rest carries over into the expansion.
   */
  function fallbackSummary() {
    var d;
    try {
      d = buildDiagnosis(buildSignals());
    } catch (e) {
      return null;
    }
    if (!d || !d.lead) return null;

    /* First sentence, keeping the abbreviation-safe simple case: these are
       hand-written strings, not arbitrary input, and none of them contain a
       full stop that is not a sentence end. */
    var cut = d.lead.indexOf('. ');
    var head = cut > 0 ? d.lead.slice(0, cut) : d.lead.replace(/[.\s]+$/, '');

    /* Mapped into the generated shape rather than rendered as its own thing,
       so a shopper on the fallback gets the same layout as everyone else — the
       words are plainer, the screen is not visibly degraded.

       Icons are assigned by position, not chosen: buildDiagnosis writes prose,
       not labelled points, and guessing a glyph per sentence would put a
       thyroid shield next to a paragraph about heat styling. Fixed order is
       honest about that. */
    var points = [];
    if (d.detail) {
      points.push({ icon: 'follicle', heading: 'What this usually means', body: d.detail });
    }
    var mech = d.mech || [];
    for (var i = 0; i < mech.length && points.length < 3; i++) {
      points.push({
        icon: i === 0 ? 'scalp' : 'strand',
        heading: i === 0 ? 'What else is in play' : 'And alongside it',
        body: mech[i]
      });
    }

    return {
      teaser: head + '…',
      lead: d.lead,
      points: points,
      /* No bridge. The generated copy earns its close by naming the doubt this
         shopper's answers imply; buildDiagnosis has no equivalent, and an
         invented one here would be the generic sales line the whole rewrite
         was meant to get away from. */
      bridge: ''
    };
  }

  /**
   * Decide once, then hold. Called on the first results render only; every
   * later render (the expand toggle re-renders) reuses what was chosen.
   */
  function shownSummary() {
    if (summary.shown !== null) return summary.shown;
    if (summary.status === 'off') {
      summary.shown = false;
      return false;
    }
    if (summary.status === 'ready' && summary.teaser && summary.lead) {
      summary.shown = {
        teaser: summary.teaser, lead: summary.lead,
        points: summary.points, bridge: summary.bridge,
        specifics: summary.specifics, timeline: summary.timeline,
        questions: summary.questions, source: 'generated'
      };
      return summary.shown;
    }
    var fb = fallbackSummary();
    summary.shown = fb
      ? { teaser: fb.teaser, lead: fb.lead, points: fb.points, bridge: fb.bridge, source: 'fallback' }
      : false;
    return summary.shown;
  }

  /* The expansion is rendered hidden rather than built on demand, and the
     toggle flips the attribute in place — same as the product list's
     [data-hq-more]. Re-rendering the results screen to open a paragraph would
     rebuild the offer panel underneath it, which is the one thing on this
     screen that should never move. */
  /**
   * The icons, owned here.
   *
   * The model picks a name from this set; it never draws one. Generated SVG
   * would be unreviewed markup going straight into the page — it can break the
   * layout, and it is an injection surface on copy that already ships without
   * a human reading it. An unknown name renders nothing rather than a broken
   * glyph, so a future icon in the enum degrades quietly until it is drawn.
   */
  var ICONS = {
    hormone:  '<circle cx="7" cy="8" r="3.2"/><circle cx="16" cy="16" r="3.2"/><path d="M9.4 10.4 13.6 13.6"/>',
    scalp:    '<path d="M3 15c0-5 4-9 9-9s9 4 9 9"/><path d="M3 18.5h18"/><path d="M7.5 11.5v.01M12 10v.01M16.5 11.5v.01"/>',
    strand:   '<path d="M6 3c0 4 3 5 3 9s-3 5-3 9"/><path d="M15 3c0 4 3 5 3 9"/><path d="M16.5 16.5 21 21M21 16.5 16.5 21"/>',
    follicle: '<path d="M12 21V9"/><path d="M12 9c0-3 2-5 5-6"/><path d="M12 13c0-2.5-2-4-4.5-4.5"/><circle cx="12" cy="21" r="1.4"/>',
    routine:  '<rect x="8" y="3" width="8" height="6" rx="2"/><path d="M9.5 9v10.5a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2V9"/><path d="M9.5 14h5"/>',
    clock:    '<circle cx="12" cy="12" r="8.5"/><path d="M12 7v5.2l3.2 2"/>',
    doctor:   '<path d="M12 3.5 4.5 6.5v5c0 4.5 3.2 8.4 7.5 9.6 4.3-1.2 7.5-5.1 7.5-9.6v-5z"/><path d="M12 9.5v5M9.5 12h5"/>',
    colour:   '<path d="M12 3.5s5.5 6 5.5 9.6A5.5 5.5 0 0 1 12 18.6a5.5 5.5 0 0 1-5.5-5.5C6.5 9.5 12 3.5 12 3.5z"/>'
  };

  function iconSvg(name) {
    var d = ICONS[name];
    if (!d) return '';
    return '<svg class="hq-analysis__ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.6" stroke-linecap="round" aria-hidden="true">' + d + '</svg>';
  }

  /**
   * The follow-up questions, as controls.
   *
   * Accepts either shape: the server sends { q, sig } objects, but a stored or
   * older response may carry plain strings. A plain string cannot be asked —
   * there is no signature to send — so it renders as text rather than as a
   * button that would come back refused. Better to look like a list than to
   * look like a control and fail.
   */
  function questionsHtml(list, label) {
    var qs = Array.isArray(list) ? list : [];
    if (!qs.length) return '';

    var out = '<p class="hq-analysis__label">' + esc(label) + '</p>' +
      '<ul class="hq-analysis__questions">';
    for (var i = 0; i < qs.length; i++) {
      var item = qs[i];
      var text = typeof item === 'string' ? item : (item && item.q) || '';
      var sig = typeof item === 'string' ? '' : (item && item.sig) || '';
      if (!text) continue;
      out += sig
        ? '<li><button type="button" class="hq-analysis__ask" data-hq-ask="' +
          esc(sig) + '" data-hq-q="' + esc(text) + '">' + esc(text) + '</button></li>'
        : '<li><span class="hq-analysis__ask-plain">' + esc(text) + '</span></li>';
    }
    return out + '</ul>';
  }

  function summaryPanel() {
    var s = shownSummary();
    if (!s) return '';

    var body = '';
    if (s.lead) body += '<p class="hq-analysis__lead">' + esc(s.lead) + '</p>';

    var pts = Array.isArray(s.points) ? s.points : [];
    if (pts.length) {
      body += '<ul class="hq-analysis__points">';
      for (var i = 0; i < pts.length; i++) {
        var pt = pts[i] || {};
        if (!pt.heading && !pt.body) continue;
        body +=
          '<li>' + iconSvg(pt.icon) +
          '<span><span class="hq-analysis__ptitle">' + esc(pt.heading || '') + '</span>' +
          '<span class="hq-analysis__pbody">' + esc(pt.body || '') + '</span></span></li>';
      }
      body += '</ul>';
    }

    /* What only she can have.
       The tap on the product page renders a lead, three points and a bridge.
       This renders those plus these two, and that difference is the entire
       reason the link back to the quiz is honest: a shopper who answered
       thirteen questions and got the paragraph she already read on the PDP has
       been told the quiz was worth taking and found out it was not. */
    if (s.specifics) {
      body +=
        '<p class="hq-analysis__label">What stands out in your answers</p>' +
        '<p class="hq-analysis__specifics">' + esc(s.specifics) + '</p>';
    }

    if (s.timeline) {
      body +=
        '<p class="hq-analysis__label">What to expect</p>' +
        '<p class="hq-analysis__timeline">' + esc(s.timeline) + '</p>';
    }

    /* The turn into the offer.
       It had its own filled panel, which was wrong twice over: a dark box put a
       border hard against the text above it so the gap read as cramped no
       matter how much margin it was given, and it fenced the one section that
       should feel like the page turning towards the product rather than a
       footnote sitting beside it.
       A label does that job instead. It names what the section is before the
       shopper reads a word of it, which is the transition the box was failing
       to make. */
    if (s.bridge) {
      body +=
        '<div class="hq-analysis__solution">' +
        '<p class="hq-analysis__solution-label">' +
        esc(get(SETTINGS, 'solution_label', 'The solution with Bloom')) +
        '</p>' +
        '<p class="hq-analysis__bridge">' + esc(s.bridge) + '</p>' +
        '</div>';
    }

    /* Askable now.
       Each question carries a signature from the server, and /api/quiz/ask
       answers nothing it did not itself write — so the signature travels with
       the button and is what makes the tap work. A question without one is not
       rendered as a control at all rather than rendered as one that fails. */
    body += questionsHtml(s.questions, 'You might also be wondering');

    /* Where answers land. Kept outside the questions list so an answer does not
       reflow the list it came from. */
    body += '<div class="hq-analysis__thread" data-hq-thread></div>';

    return (
      '<section class="hq-analysis">' +
      '<p class="hq-analysis__teaser">' + esc(s.teaser) + '</p>' +
      '<div class="hq-analysis__body" data-hq-analysis-body hidden>' + body + '</div>' +
      '<button type="button" class="hq-analysis__more" data-hq-analysis aria-expanded="false">' +
      'Read full analysis</button>' +
      '</section>'
    );
  }

  function writeUnlock() {
    var name = String(DISCOUNT.unlock_cookie || '_fleur_quiz_unlock');
    var days = Number(DISCOUNT.unlock_days) || 90;
    try {
      var d = new Date();
      d.setTime(d.getTime() + days * 864e5);
      document.cookie =
        name + '=1;expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
    } catch (e) {}

    /* Clear the previous window's deadline.

       assets/quiz-earned.js stamps _fleur_quiz_until when it first shows the
       earned price, and both that file and ab-assign.liquid read it as "this
       offer lapses then". Left behind from an earlier run it is a deadline
       already in the past, so a shopper who came back and took the quiz again
       would have the new discount expire the instant it was granted, and be
       told it had expired on a page they had just finished earning it on. */
    try {
      document.cookie =
        '_fleur_quiz_until=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax';
    } catch (e) {}

    /* And tell the page she is standing on.

       The attribute is written server-side from this cookie, which was fine
       while the quiz was a page of its own and finishing it meant a navigation
       back. In a panel there is no navigation: she closes the sheet onto the
       same document that rendered before she started, still saying she has not
       taken the quiz, still offering to let her. Everything downstream keys off
       this attribute — the earned prices, the countdown, the drawer banner — so
       setting it is what makes the page agree with what just happened. */
    try {
      document.documentElement.setAttribute('data-quiz-unlock', 'yes');
      document.documentElement.removeAttribute('data-quiz-expired');
      document.dispatchEvent(new CustomEvent('quiz:unlocked', { bubbles: true }));
    } catch (e) {}
  }

  /* The SMS bonus, recorded separately so the product page can name the whole
     discount before there is a cart to read it from.

     The unlock cookie above is '=1' and is written at the email step, before
     the phone step has happened — it records that the quiz was finished, not
     what was won. So a completer who came back later with an empty cart was
     told "You earned 17% off" when they had earned 21%: the bonus is a
     cart-level code, and with nothing in the cart there is nothing for it to
     be allocated against and nothing to read.

     A second cookie rather than a value in the first: the existing one holds
     '1' for every shopper who has already taken the quiz, and a '1' that used
     to mean "unlocked" would silently start meaning "1% bonus".

     This governs the percentage only. The money still comes from the cart —
     a code can be spent, expire or be withdrawn, and a figure taken from a
     cookie is a promise nothing has checked. */
  function writeBonus() {
    var days = Number(DISCOUNT.unlock_days) || 90;
    var pct = state.phone && PHONE_CODE ? Math.round(PHONE_BONUS) : 0;
    if (!(pct > 0)) return;
    try {
      var d = new Date();
      d.setTime(d.getTime() + days * 864e5);
      document.cookie =
        '_fleur_quiz_bonus=' + pct + ';expires=' + d.toUTCString() +
        ';path=/;SameSite=Lax';
    } catch (e) {}
  }

  function save() {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        /* The phone is persisted for the same reason the email is: everything
           downstream of the results screen reads it back. Without it a reload
           silently cost the shopper their SMS bonus — the headline fell from
           22% to 17%, the bonus line disappeared, and the subscribe button
           stopped re-applying the discount code, because all three ask
           state.phone and it was empty on every visit after the first.

           Their own number, on their own device, beside their own email. It is
           never read except to restore this session. */
        JSON.stringify({
          index: state.index,
          answers: state.answers,
          email: state.email,
          phone: state.phone
        })
      );
    } catch (e) {}
  }

  function restore() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var parsed = JSON.parse(raw);
      if (parsed && parsed.answers) {
        state.answers = parsed.answers;
        state.email = parsed.email || '';
        state.phone = parsed.phone || '';
        /* Resume mid-flow, but never straight back onto the results screen —
           those are regenerated from answers, not stored. */
        var idx = Number(parsed.index) || 0;
        var step = STEPS[idx];
        if (step && step.kind !== 'results' && step.kind !== 'analysing') {
          state.index = idx;
        }
      }
    } catch (e) {}
  }

  function clearSaved() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  /* Steps whose showIf fails are skipped in both directions. */
  /* The quiz is now answered live rather than from a warmed row, and a full
     analysis measures around forty seconds. The analysing screen is nowhere
     near that, so the request goes out the moment the last real question is
     answered — after `commitment`, with the email step, the phone step and the
     analysing screen left to run underneath it.

     That is the only point where both things are true: every answer exists, and
     there is still screen time left to spend. Firing earlier would mean writing
     an analysis without her scalp, her texture or what she has tried — the very
     answers that make it worth calling full. */
  function maybeStartAnalysis() {
    var a = state.answers || {};
    if (a.commitment && a.commitment.length) requestSummary();
  }

  function visible(step) {
    if (!step) return false;
    if (typeof step.showIf !== 'function') return true;
    return !!step.showIf(state.answers);
  }

  function nextIndex(from) {
    for (var i = from + 1; i < STEPS.length; i++) {
      if (visible(STEPS[i])) return i;
    }
    return STEPS.length - 1;
  }

  function prevIndex(from) {
    for (var i = from - 1; i >= 0; i--) {
      if (visible(STEPS[i])) return i;
    }
    return 0;
  }

  /**
   * Two different measures, deliberately.
   *
   * The COUNT shown to the shopper is questions only. Interstitials and the
   * email step are screens, not questions, and folding them in turns a
   * twelve-question quiz into "1 / 17" on the very first screen — which reads
   * as a chore and costs completions.
   *
   * The BAR is the true position across every remaining screen, so it keeps
   * moving on the interstitials rather than appearing to stall.
   *
   * Both count only steps this shopper will actually see, so someone who
   * skips the postpartum branch is never promised a question that never comes.
   */

  /* =========================================================================
     Answers -> signals
     ========================================================================= */

  function buildSignals() {
    var a = state.answers;
    var life = a.lifestage || [];
    var concerns = a.concern || [];
    /* The separate "what would make this worth it?" question was cut: it
       restated the concerns almost one-for-one, so it cost a screen and told
       us nothing new. Goals are now derived from what the shopper already
       said is wrong, which keeps every goal-based rule below working. */
    var concernGoals = {
      thinning: ['density'],
      shedding: ['shedding'],
      breakage: ['strength', 'length'],
      flat: ['density'],
      scalp: ['scalp'],
      edges: ['edges', 'density']
    };
    var goals = [];
    for (var ci = 0; ci < concerns.length; ci++) {
      var mapped = concernGoals[concerns[ci]];
      if (!mapped) continue;
      for (var gi = 0; gi < mapped.length; gi++) {
        if (goals.indexOf(mapped[gi]) === -1) goals.push(mapped[gi]);
      }
    }
    var scalpArr = a.scalp || [];
    var damageArr = a.damage || [];

    /* Persona resolves in priority order. A recent birth is the strongest
       explanation available and should win over a family-history tick. */
    var persona = 'general';
    if (has(life, ['postpartum'])) persona = 'postpartum';
    else if (has(life, ['menopause'])) persona = 'menopause';
    /* A named medical cause outranks a family history: it is the more
       specific and more actionable explanation for what is happening now. */
    else if (has(life, ['medical'])) persona = 'medical';
    else if (has(life, ['stress', 'diet'])) persona = 'telogen';
    else if (has(life, ['family'])) persona = 'hereditary';

    /* Several options were merged to shorten the quiz. The signal shape is
       kept intact and the merged flags alias their surviving option, so the
       scoring rules below did not have to learn about the consolidation.
       `itchy` and `flaky` are one option now, as are `oily` and `buildup`. */
    var scalp = {
      oily: scalpArr.indexOf('oily') !== -1,
      dry: scalpArr.indexOf('dry') !== -1,
      flaky: scalpArr.indexOf('flaky') !== -1,
      itchy: scalpArr.indexOf('flaky') !== -1,
      sensitive: scalpArr.indexOf('sensitive') !== -1,
      buildup: scalpArr.indexOf('oily') !== -1,
      balanced: scalpArr.indexOf('balanced') !== -1
    };

    /* Likewise bleach folded into colour, and extensions into tight styles. */
    var damage = {
      heat: damageArr.indexOf('heat') !== -1,
      color: damageArr.indexOf('color') !== -1,
      bleach: damageArr.indexOf('color') !== -1,
      chemical: damageArr.indexOf('chemical') !== -1,
      tension: damageArr.indexOf('tension') !== -1,
      extensions: damageArr.indexOf('tension') !== -1
    };

    var texture = (a.texture || [])[0] || 'medium';
    if (texture === 'unsure') texture = 'medium';

    return {
      persona: persona,
      lifestage: life,
      concerns: concerns,
      goals: goals,
      pattern: (a.pattern || [])[0] || '',
      phase: (a.duration || [])[0] || '',
      hairtype: (a.hairtype || [])[0] || '',
      texture: texture,
      density: texture === 'coarse' ? 'dense' : '',
      scalp: scalp,
      damage: damage,
      wash: '', /* question removed; kept so rules referencing it stay valid */
      treatments: (a.treatments || [])[0] || '',
      commitment: (a.commitment || [])[0] || '',
      postpartumWhen: (a.postpartum_when || [])[0] || '',
      menopauseStage: (a.menopause_stage || [])[0] || ''
    };
  }

  /* =========================================================================
     Scoring
     ========================================================================= */

  function scoreProducts(signals) {
    var out = [];

    for (var i = 0; i < PRODUCTS.length; i++) {
      var p = PRODUCTS[i];
      var product = CATALOG[p.key];

      /* No live product, no card. Keeps the engine honest if a SKU is pulled
         or renamed in Shopify without anyone touching this file. */
      if (!product || !product.available) continue;
      if (typeof p.veto === 'function' && p.veto(signals)) continue;

      var best = 0;
      var because = '';
      var matches = 0;

      for (var r = 0; r < p.rules.length; r++) {
        var rule = p.rules[r];
        if (!rule.when(signals)) continue;
        matches++;
        if (rule.score > best) {
          best = rule.score;
          because = rule.because;
        }
      }

      /* Several independent reasons to recommend something is itself a
         signal, but it must not let a pile of weak matches beat one strong
         one — hence a small, capped bonus. */
      if (matches > 1) best += Math.min(matches - 1, 2);

      if (p.hero) {
        out.push({
          key: p.key,
          product: product,
          hero: true,
          flag: p.flag,
          score: 100,
          because: because || p.fallback
        });
        continue;
      }

      if (best < SCORE_FLOOR) continue;

      out.push({
        key: p.key,
        product: product,
        hero: false,
        score: best,
        because: because || p.fallback
      });
    }

    out.sort(function (a, b) {
      return b.score - a.score;
    });

    return out;
  }

  /* =========================================================================
     Narrative — the read-back above the routine
     ========================================================================= */

  function buildDiagnosis(signals) {
    var lead = '';
    var detail = '';

    if (signals.persona === 'postpartum') {
      var when = signals.postpartumWhen;
      lead =
        'What you’re seeing has a name: postpartum telogen effluvium. During pregnancy, raised oestrogen holds hairs in their growth phase far longer than usual. After birth those hormones drop and all of that held hair leaves at once.';
      if (when === 'pp_0_3') {
        detail =
          'At this stage you may be just ahead of the peak, which typically lands around month three. It is temporary — but the routine you build now is what determines how densely it grows back.';
      } else if (when === 'pp_3_6' || when === 'pp_6_12') {
        detail =
          'You’re in the window where shedding usually starts to settle and regrowth begins. Those short, fine baby hairs along your hairline are new growth, not breakage — supporting them now is the whole game.';
      } else {
        detail =
          'Past twelve months, most postpartum shedding has resolved on its own. If density hasn’t returned, it’s worth treating this as an ongoing pattern rather than waiting it out.';
      }
    } else if (signals.persona === 'menopause') {
      lead =
        'Falling oestrogen shortens the growth phase of each hair and shrinks the follicle a little with every cycle. The result is gradual, diffuse thinning that shows up first where the hair parts.';
      detail =
        'This one doesn’t resolve by itself, which is exactly why a consistent daily scalp routine matters more here than at any other life stage.';
    } else if (signals.persona === 'telogen') {
      lead =
        'A physical stressor — illness, sustained stress, a sharp change in diet — can push a large share of follicles out of their growth phase at once. The shed usually shows up two to three months after the event, which is why it so often feels like it came from nowhere.';
      detail =
        'The encouraging part: these follicles are dormant, not gone. A supportive scalp environment is what shortens the gap before they cycle back in.';
    } else if (signals.persona === 'hereditary') {
      lead =
        'A family history combined with where you’re noticing it points towards a gradual, inherited pattern. Follicles miniaturise over successive cycles, producing finer and shorter hairs each time until they stop altogether.';
      detail =
        'Consistency is everything with this pattern. It responds far better to daily support started early than to intensive effort started late.';
    } else if (signals.persona === 'medical') {
      lead =
        'Thyroid conditions, PCOS and a number of common medications all affect the hair cycle directly. That makes the underlying condition the first thing to address — worth raising with your doctor if you haven’t.';
      detail =
        'Alongside that, a daily scalp routine supports the environment your follicles are trying to recover in. The two work together rather than in place of each other.';
    } else {
      lead =
        'Nothing in your answers points to a single obvious trigger, which is common — and usually means several smaller loads are stacking up: daily mechanical stress, scalp condition, and the ordinary slow drift of the hair cycle.';
      detail =
        'That’s a workable position. It means the routine below is targeting real, addressable inputs rather than fighting one dominant cause.';
    }

    var mech = [];
    if (signals.damage.bleach || signals.damage.chemical) {
      mech.push(
        'Bleach and chemical services permanently alter the cuticle, so the hair you already have needs protecting as much as new growth needs encouraging.'
      );
    } else if (signals.damage.heat || signals.damage.color) {
      mech.push(
        'Regular heat and colour weaken the strand, which means some of what reads as thinning is length lost to breakage rather than density lost at the root.'
      );
    }
    if (signals.damage.tension || signals.damage.extensions) {
      mech.push(
        'Tight styles and extensions pull constantly at the hairline. Over time that tension alone can thin the edges, independently of anything hormonal.'
      );
    }
    if (signals.scalp.flaky || signals.scalp.itchy) {
      mech.push(
        'An itchy, flaking scalp is an inflamed one, and inflammation at the follicle interferes with growth. Calming it is a prerequisite, not an extra.'
      );
    } else if (signals.scalp.oily || signals.scalp.buildup) {
      mech.push(
        'Oil and product buildup sit between anything you apply and the skin you are applying it to — the most common reason a good serum underperforms.'
      );
    }

    return { lead: lead, detail: detail, mech: mech };
  }

  /* Short chips summarising what we took from the answers. */
  function buildReadback(signals) {
    var chips = [];
    var labelFor = function (stepId, optId) {
      for (var i = 0; i < STEPS.length; i++) {
        if (STEPS[i].id !== stepId || !STEPS[i].options) continue;
        for (var j = 0; j < STEPS[i].options.length; j++) {
          if (STEPS[i].options[j].id === optId) return STEPS[i].options[j].label;
        }
      }
      return '';
    };

    var personaLabel = {
      postpartum: 'Postpartum',
      menopause: 'Menopausal transition',
      telogen: 'Stress-related shed',
      hereditary: 'Hereditary pattern',
      medical: 'Medical factor'
    };
    if (personaLabel[signals.persona]) chips.push(personaLabel[signals.persona]);

    if (signals.hairtype) chips.push(labelFor('hairtype', signals.hairtype) + ' hair');
    if (signals.texture) chips.push(signals.texture.charAt(0).toUpperCase() + signals.texture.slice(1) + ' strands');
    if (signals.pattern) chips.push(labelFor('pattern', signals.pattern));

    var scalpBits = [];
    if (signals.scalp.oily) scalpBits.push('oily');
    if (signals.scalp.dry) scalpBits.push('dry');
    if (signals.scalp.flaky) scalpBits.push('flaky');
    if (signals.scalp.sensitive) scalpBits.push('sensitive');
    if (scalpBits.length) {
      /* Sentence-case so this chip matches the Title Case of its neighbours. */
      var scalpText = scalpBits.join(', ') + ' scalp';
      chips.push(scalpText.charAt(0).toUpperCase() + scalpText.slice(1));
    } else if (signals.scalp.balanced) {
      chips.push('Balanced scalp');
    }

    if (signals.damage.bleach) chips.push('Bleached');
    else if (signals.damage.color) chips.push('Colour-treated');
    if (signals.damage.heat) chips.push('Heat styling');
    if (signals.damage.tension || signals.damage.extensions) chips.push('Tension styling');

    var out = [];
    for (var i = 0; i < chips.length; i++) {
      if (chips[i] && out.indexOf(chips[i]) === -1) out.push(chips[i]);
    }
    return out;
  }

  /* =========================================================================
     Rendering
     ========================================================================= */

  var stage = root.querySelector('[data-hq-stage]');
  var railBack = root.querySelector('[data-hq-back]');
  var progressFill = root.querySelector('[data-hq-progress]');

  /* 17 not 17.00, and 2.1 not 2.1111. One decimal is the most a discount can
     usefully carry — the exact figure still drives the arithmetic, this only
     decides what is printed. */
  function trimPct(n) {
    var r = Math.round(Number(n) * 10) / 10;
    return String(r);
  }

  /* The tally and the button badges show whole numbers. The underlying pool
     does not divide evenly — 26 points over 9 questions is 2.888 each — but a
     running total is a score, and a score with a decimal in it reads as a
     measurement. The true value is kept intact underneath; only the display
     rounds. */
  function wholePct(n) {
    return String(Math.round(Number(n) || 0));
  }

  /* The meter row, the number at each end. `earnedEl` is both the running
     total and the target the reward flies into. */
  var meterEl = root.querySelector('[data-hq-meter]');
  var meterTrack = root.querySelector('[data-hq-meter-track]');
  var earnedEl = root.querySelector('[data-hq-earned]');
  var targetEl = root.querySelector('[data-hq-target]');
  if (meterTrack) meterTrack.setAttribute('aria-valuemax', wholePct(TOTAL));

  /* What the tally is currently showing. Held separately from what has actually
     been earned so the number can lag behind the answer: while a reward is in
     flight the old total stays on screen, and the new one lands when the coin
     does. Without this the tally would tick up the instant the button is
     pressed and the animation would be delivering something already delivered. */
  var shownPct = 0;

  /**
   * How far through the questions she is, as a fraction.
   *
   * Only the steps she will actually be asked count. Visibility depends on
   * earlier answers, so the denominator is recomputed rather than fixed: a
   * branch she has skipped past was never part of her walk and should not sit
   * in the total making her look further from the end than she is.
   */
  function visibleQuestions() {
    var total = 0;
    var done = 0;
    for (var i = 0; i < STEPS.length; i++) {
      var st = STEPS[i];
      if (st.kind !== 'single' && st.kind !== 'multi') continue;
      if (!visible(st)) continue;
      total++;
      var a = state.answers[st.id];
      if (a !== undefined && a !== null && a !== '' && !(a.length === 0)) done++;
    }
    return { total: total, done: done };
  }

  function questionProgress() {
    var seen = visibleQuestions();
    return seen.total > 0 ? seen.done / seen.total : 0;
  }

  /**
   * What the meter reports.
   *
   * Tied to her progress through the flow, not to a subset of it. The pool used
   * to be split across the questions named in discount_earning_steps, which
   * meant most of what she answered moved nothing: she would give three answers
   * and watch the number sit still, which reads as the quiz not counting her
   * work rather than as this question happening not to be one of the scoring
   * ones. A meter that stalls is worse than no meter, because it actively
   * reports that going on is not worth it.
   *
   * So every question she is asked carries an equal share of the same pool, and
   * the last one lands her exactly where the old scheme did. Nothing about the
   * discount she actually earns changes — the endpoints are identical, only the
   * walk between them is even.
   */
  /* What the meter counts down from, and what it counts down to.

     A percentage is a fact about the offer; a price is a fact about her. "17%"
     asks her to do arithmetic against a number she has to remember, and the
     bar beside it is then measuring the arithmetic rather than the saving. Two
     prices need no working out at all: the one on the left is what she would
     pay standing still, the one on the right is what she is walking toward,
     and the distance between them is the only thing the bar has to mean.

     The walk is interpolated across the same fraction that drives the fill, so
     the number and the bar cannot disagree, and the right-hand end is the real
     charge from the plan rather than a percentage applied to a list price —
     what she reads here is what the results panel will ask her to pay. */
  var METER_FALLBACK = Number(DISCOUNT.meter_full_price) || 5800;

  /* Per plan, because the plans are not variations on one price: the ladder
     runs $58, $174, $348 a delivery at full price against $48, $132, $240
     discounted. A single starting figure meant the six-month meter opening at
     $58 and climbing towards $228 — the bar filled while the number went up. */
  function meterFull() {
    var plan = chosenPlan();
    var full = plan ? Number(plan.full_price) : 0;
    return full > 0 ? full : METER_FALLBACK;
  }

  function meterBest() {
    var plan = chosenPlan();
    if (!plan) return null;
    var price = Number(plan.price);
    if (!price) return null;
    /* The phone bonus is part of the destination whenever it is on offer: the
       far end of the bar is the best price reachable, not the price before the
       last step. */
    return PHONE_CODE ? Math.round(price * (1 - PHONE_BONUS / 100)) : price;
  }

  /* The struck price is fixed: it is what the subscription costs without the
     quiz, and nothing she does changes it. Written once, and shown only once
     there is a discount for it to be struck against. */
  if (earnedEl) earnedEl.textContent = money(meterFull());
  if (targetEl) targetEl.removeAttribute('aria-hidden');

  /* Where the price stands between the two ends.

     Hinged on the plan's own price at the milestone rather than stretched
     across the whole span: the questions and the email are worth 17 of the 22
     points but rather more than 17/22 of the money, and straight interpolation
     put the email milestone at $48.42 against a real charge of $48.00. Two
     segments, so every point she can actually stop at is exact. */
  /* Drives whichever gauge the markup happens to be.

     A ring is filled by shortening the dash that hides it, not by setting a
     width, and the length to shorten depends on its radius — so the geometry
     is read off the element rather than hardcoded, and a bar still works if
     the markup goes back to one. */
  function setFill(pct) {
    if (!progressFill) return;
    var tag = (progressFill.tagName || '').toLowerCase();
    if (tag === 'circle') {
      var r = Number(progressFill.getAttribute('r')) || 0;
      var circumference = 2 * Math.PI * r;
      progressFill.style.strokeDasharray = circumference;
      progressFill.style.strokeDashoffset = circumference * (1 - pct / 100);
      return;
    }
    progressFill.style.width = pct + '%';
  }

  /* How finely the falling figure is rounded.

     Every answer has to move it. On the one-month ladder twelve questions
     split ten dollars, so each is worth 83c — and snapped to the dollar, two
     of the twelve landed on the number already showing. She answers, and the
     reward for answering is that nothing happens.

     Fifty cents clears it: no two consecutive answers share a figure. The
     longer plans do not need the precision — six months moves nine dollars a
     question — so the grain is chosen from what one answer is actually worth
     rather than fixed, and those ladders keep their whole dollars. */
  function grain() {
    var steps = visibleQuestions().total;
    if (steps < 1) return 100;
    /* Across the span the questions actually cover, which is not the span down
       to the plan price: the email takes 8 of the 17 points on the one-month
       ladder, so twelve questions share $5.50 rather than $10 — about 44c
       each. Measured against the wrong span the grain came out at 50c, coarser
       than the step it was meant to resolve, and answers collided again. */
    var top = rawPrice(0);
    var bottom = rawPrice(QUESTION_POOL);
    if (top === null || bottom === null) return 100;
    var per = (top - bottom) / steps;
    if (per >= 150) return 100;
    if (per >= 75) return 50;
    return 25;
  }

  function dollars(cents) {
    var g = grain();
    return Math.round(cents / g) * g;
  }

  /* Unrounded, so that the grain can be chosen by measuring it. */
  function rawPrice(pct) {
    var plan = chosenPlan();
    var mid = plan ? Number(plan.price) : 0;
    if (!mid) return null;
    var best = PHONE_CODE ? Math.round(mid * (1 - PHONE_BONUS / 100)) : mid;
    var full = meterFull();
    if (pct <= 0) return full;
    if (pct >= TOTAL) return best;
    if (pct <= TARGET || TOTAL <= TARGET) {
      return full - (full - mid) * (TARGET > 0 ? pct / TARGET : 0);
    }
    return mid - (mid - best) * ((pct - TARGET) / (TOTAL - TARGET));
  }

  function meterPrice(pct) {
    var raw = rawPrice(pct);
    if (raw === null) return null;
    /* The milestones keep their exact figures — they are prices someone is
       actually charged. Only the walk between them is rounded to something
       readable. */
    if (pct <= 0 || pct >= TOTAL || pct === TARGET) return Math.round(raw);
    return dollars(raw);
  }

  /* One number, and it falls.

     Two static ends said what the offer was; a single figure counting down
     says what answering is doing. It opens at the full price, so the first
     thing she reads is what the subscription costs without her, and every
     answer takes money off it in front of her. The bar underneath is then the
     distance left rather than a second reading of the same fact.

     Counted rather than swapped. A number that jumps from $58 to $55.50 has
     changed; a number seen falling has been *reduced*, and it is the falling
     that reads as the reward. */
  var reelEl = root.querySelector('[data-hq-reel]');
  var reelPrices = null;
  var priceShown = null;
  var priceSettle = null;

  /* Every price she can still reach, in the order she reaches them.

     Built from the questions she will actually be asked, then the email and
     the phone — the same three-stage walk meterPrice already describes, simply
     enumerated instead of sampled. Rebuilt when that count changes, which a
     branch opening can do; otherwise it is stable for the whole quiz. */
  function buildReel() {
    var n = visibleQuestions().total;
    var stages = [];
    for (var i = 0; i <= n; i++) {
      stages.push(meterPrice(n > 0 ? QUESTION_POOL * (i / n) : 0));
    }
    stages.push(meterPrice(TARGET));
    stages.push(meterPrice(TOTAL));

    /* Two stages can still land on one figure, and a queue showing the same
       price twice reads as a stall. The duplicate is dropped and its stage
       points at the tick already there, so the reel holds for that answer
       rather than sliding into a slot that changes nothing. */
    var ticks = [];
    var at = [];
    for (var k = 0; k < stages.length; k++) {
      if (!ticks.length || ticks[ticks.length - 1] !== stages[k]) ticks.push(stages[k]);
      at.push(ticks.length - 1);
    }
    return { ticks: ticks, at: at };
  }

  /* Which of them she is standing on. */
  function reelAt() {
    var n = visibleQuestions().total;
    if (state.phone && PHONE_CODE) return n + 2;
    if (state.email) return n + 1;
    return Math.min(visibleQuestions().done, n);
  }

  function paintReel(animate) {
    if (!reelEl) return;
    var built = buildReel();
    var want = built.ticks;
    var stale = !reelPrices || reelPrices.length !== want.length ||
      reelPrices.join() !== want.join();

    if (stale) {
      reelPrices = want;
      var html = '';
      for (var i = 0; i < want.length; i++) {
        html += '<span class="hq-progress__tick">' + esc(money(want[i])) + '</span>';
      }
      reelEl.innerHTML = html;
    }

    var ticks = reelEl.children;
    var stage = Math.max(0, Math.min(built.at.length - 1, reelAt()));
    var at = Math.max(0, Math.min(ticks.length - 1, built.at[stage]));
    /* Three roles, taken by position: the price she has, the one she just
       left — struck, on its way out to the left — and everything further back,
       which holds its space but is not drawn. Each figure passes through all
       of them in turn, which is what makes it one motion rather than a queue
       moving past a separate label. */
    for (var k = 0; k < ticks.length; k++) {
      ticks[k].classList.toggle('is-now', k === at);
      ticks[k].classList.toggle('is-was', k === at - 1);
      ticks[k].classList.toggle('is-gone', k < at - 1);
    }

    var live = ticks[at];
    var window_ = reelEl.parentNode;
    if (!live || !window_) return;

    /* Centred on the window rather than pushed to its edge, so the figure she
       has sits on the middle of the panel and the queue runs away to the right
       of it. Measured from the tick itself, because the offset depends on how
       wide this particular price is — $58 and $125.40 do not centre in the
       same place. */
    var mid = window_.offsetWidth / 2;
    var shift = mid - (live.offsetLeft + live.offsetWidth / 2);

    var held = reelEl.style.transition;
    if (!animate || stale) reelEl.style.transition = 'none';
    reelEl.style.transform = 'translateX(' + shift + 'px)';
    if (!animate || stale) {
      void reelEl.offsetWidth;
      reelEl.style.transition = held || '';
    }

  }

  /* The amount just taken off, thrown out beside the dial.

     The falling number says the price is lower than it was; it does not say
     how much she just won, and "how much did that get me" is the question a
     reward has to answer immediately. The chip does it in the one place she is
     already looking, then leaves — it is a receipt for the last answer, not a
     running total, and the ring keeps the total.

     Cleared to the right of the ring rather than over it: the count is the
     main event and nothing should cross it. */
  function flashDelta(cents) {
    if (!meterEl || cents <= 0 || prefersReduce()) return;
    /* Anchored to the price column, which is the positioned box now that the
       row itself is only a centring device. Left on the row, the chip would
       have hung off the full width of the panel instead of off the number. */
    var host = meterEl.querySelector('.hq-progress__new');
    if (!host) return;
    var chip = document.createElement('span');
    chip.className = 'hq-progress__delta';
    chip.setAttribute('aria-hidden', 'true');
    chip.textContent = '−' + money(cents);
    host.appendChild(chip);
    window.setTimeout(function () {
      if (chip.parentNode) chip.parentNode.removeChild(chip);
    }, 1600);
  }

  function paintPrice(pct, animate) {
    var to = meterPrice(pct);
    if (to === null) return;
    var from = priceShown === null ? to : priceShown;
    priceShown = to;

    /* The announced figure, for anyone not watching the reel. */
    if (targetEl) targetEl.textContent = money(to);

    if (!animate || from === to || prefersReduce()) {
      paintReel(false);
      if (meterEl) {
        meterEl.classList.remove('is-counting');
      }
      return;
    }

    /* The whole instrument performs the drop: the reel slides a place, the
       live figure lights, and the amount just taken off flies out beside it. */
    flashDelta(from - to);
    paintReel(true);
    if (meterEl) {
      meterEl.classList.add('is-counting');
    }
    window.clearTimeout(priceSettle);
    priceSettle = window.setTimeout(function () {
      if (meterEl) meterEl.classList.remove('is-counting');
    }, 780);
  }

  var earnedFloor = 0;

  function earnedPercent() {
    var earned = Math.min(QUESTION_POOL, QUESTION_POOL * questionProgress());
    /* Never backwards.

       The denominator is what she will be asked, and that changes as she
       answers: a branch can open three follow-ups that were not in the total a
       moment ago, and the same work is suddenly a smaller share of a longer
       walk. Arithmetically fair, and unreadable — a discount meter that drops
       after an answer says the answer cost her something.

       Going back through the quiz has the same shape. The high-water mark is
       what she has earned; revisiting a question is not un-earning it. */
    if (earned > earnedFloor) earnedFloor = earned;
    earned = earnedFloor;
    if (state.email) earned = TARGET;
    if (state.phone && PHONE_CODE) earned = TARGET + PHONE_BONUS;
    return earned;
  }

  /**
   * The meter, drawn from what has been earned.
   *
   * It shows from the first question at 0% rather than waiting for something
   * to count. The whole point of a bar with 0% at one end and the offer at the
   * other is that the gap is visible before it starts closing — a meter that
   * only appears once it has something to report cannot show you the distance
   * it is going to cover.
   */
  function paintMeter(step) {
    if (!meterEl) return;
    var hide = step.kind === 'intro' || step.kind === 'analysing' || step.kind === 'results';
    meterEl.hidden = hide;
    if (hide) return;


    paintPrice(shownPct, true);
    /* Runs to TOTAL, so the dial only closes once the phone bonus is in. The
       clamp stays as a guard rather than a mechanism. */
    var pct = TOTAL > 0 ? Math.max(0, Math.min(100, (shownPct / TOTAL) * 100)) : 0;
    setFill(pct);
    if (meterTrack) meterTrack.setAttribute('aria-valuenow', wholePct(shownPct));
  }

  /**
   * Send the reward from the button to the tally.
   *
   * A clone rather than the badge itself: the step re-renders immediately on
   * Continue, so the original is destroyed mid-flight. The clone is fixed to
   * the viewport and appended to the root, which puts it outside everything the
   * render replaces.
   *
   * Navigation is not delayed for it. The next question paints straight away
   * and the coin travels over the top — waiting for an animation to finish
   * before advancing is how a reward turns into a toll.
   *
   * The arc comes from a midpoint keyframe lifted above the straight line
   * between the two points. A linear tween reads as a UI element being
   * repositioned; the lift is what makes it read as something thrown.
   */
  /* Snapshot a badge's position and label while it is still on screen.

     This has to happen BEFORE the step re-renders. render() detaches the old
     screen, and a detached element measures as a 0x0 box at the origin — which
     is exactly what a coin flying from nowhere, sized nothing, looks like. */
  function badgeOrigin(el) {
    if (!el) return null;
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    /* Carry the badge's own painted appearance, not just its box. The coin
       flies on document.body, which is outside the scope the quiz palette is
       declared on — var(--hq-sand) resolves to nothing out there and the coin
       inherits the theme's grey, invisible against the dark background. */
    var cs = window.getComputedStyle(el);
    return {
      left: r.left, top: r.top, width: r.width, height: r.height,
      text: el.textContent,
      color: cs.color,
      font: cs.font || (cs.fontWeight + ' ' + cs.fontSize + '/' + cs.lineHeight + ' ' + cs.fontFamily)
    };
  }

  function flyReward(origin, onLanded) {
    if (!REWARD_FLIGHT) return onLanded();
    if (!origin || !meterEl || !earnedEl) return onLanded();

    var coin = document.createElement('span');
    if (prefersReduce() || typeof coin.animate !== 'function') return onLanded();

    /* The tally may still be hidden on the first award, and a hidden element
       has no box to aim at. Reveal it empty for the duration of the flight. */
    var wasHidden = meterEl.hidden;
    if (wasHidden) meterEl.hidden = false;
    /* The number, not the row. The row spans the full width, so its centre is
       the middle of the screen — the coin would land nowhere near the figure
       it is being added to. */
    var to = earnedEl.getBoundingClientRect();
    if (wasHidden) meterEl.hidden = true;

    coin.className = 'hq-coin';
    coin.setAttribute('aria-hidden', 'true');
    coin.textContent = origin.text;
    coin.style.left = origin.left + 'px';
    coin.style.top = origin.top + 'px';
    coin.style.width = origin.width + 'px';
    coin.style.height = origin.height + 'px';
    if (origin.color) coin.style.color = origin.color;
    if (origin.font) coin.style.font = origin.font;
    /* On the body, not the quiz root: pressing Continue re-renders the stage
       mid-arc, and anything parented inside it gets swept away with the screen
       it came from. */
    document.body.appendChild(coin);

    var fromCx = origin.left + origin.width / 2;
    var fromCy = origin.top + origin.height / 2;
    var dx = (to.left + to.width / 2) - fromCx;
    var dy = (to.top + to.height / 2) - fromCy;

    /* Bow the midpoint perpendicular to the line of travel.

       The CTA sits bottom-centre and the tally top-right, so that line is
       nearly vertical. Lifting the midpoint straight up — the obvious move —
       buys no curve at all on a vertical path: it only front-loads the climb,
       and the coin reads as shooting up and stalling. Pushing sideways off the
       line is what actually bends it. */
    var len = Math.sqrt(dx * dx + dy * dy) || 1;
    var bow = Math.min(len * 0.22, 96);
    /* Perpendicular, taken on the side the coin is already travelling towards,
       so the throw opens away from the straight line rather than cutting back
       across it. */
    var side = dx >= 0 ? 1 : -1;
    var mx = dx / 2 + (-dy / len) * bow * side;
    var my = dy / 2 + (dx / len) * bow * side;

    /* Keep the apex on screen — a bow wide enough to leave the viewport is a
       coin that simply vanishes for half its flight. */
    var margin = origin.width / 2 + 8;
    var limit = (window.innerWidth || 390) - margin;
    if (fromCx + mx > limit) mx = limit - fromCx;
    if (fromCx + mx < margin) mx = margin - fromCx;

    var anim = coin.animate(
      [
        { transform: 'translate(0px, 0px) scale(1)', opacity: 1, easing: 'cubic-bezier(0.3, 0, 0.5, 1)' },
        /* Launch eases out into the apex, then falls in — the two halves carry
           different easings so the throw has weight at both ends. */
        { transform: 'translate(' + mx + 'px, ' + my + 'px) scale(1.2)', opacity: 1, offset: 0.5, easing: 'cubic-bezier(0.5, 0, 0.7, 1)' },
        { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(0.4)', opacity: 1, offset: 0.82 },
        { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(0.3)', opacity: 0 }
      ],
      { duration: 760, fill: 'forwards' }
    );

    var settled = false;
    var settle = function () {
      if (settled) return;
      settled = true;
      if (coin.parentNode) coin.parentNode.removeChild(coin);
      onLanded();
    };
    anim.onfinish = settle;
    /* A dropped onfinish — a backgrounded tab, an interrupted animation — must
       not strand the tally on a stale number. */
    window.setTimeout(settle, 1100);
  }

  function prefersReduce() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  function collect(step, origin) {
    var before = shownPct;
    flyReward(origin, function () {
      shownPct = earnedPercent();
      if (shownPct === before) return;
      if (meterEl) meterEl.hidden = false;
      /* Guarded like its neighbours: flyReward hands control straight to this
         callback when there is no meter to fly at. */
      paintPrice(shownPct, true);
      /* The dial moves with the number inside it. */
      if (TOTAL > 0) setFill(Math.max(0, Math.min(100, (shownPct / TOTAL) * 100)));
      if (meterTrack) meterTrack.setAttribute('aria-valuenow', wholePct(shownPct));
    });
  }

  function paintChrome(step) {
    /* The fill is the discount, not the step count. Those two ran at different
       rates — the email step is worth several questions — so a bar driven by
       position disagreed with the numbers printed at either end of it. */
    paintMeter(step);

    onFinalStep = step.kind === 'results';
    syncCartButton();

    if (railBack) {
      railBack.hidden = step.kind === 'intro' || step.kind === 'analysing' || step.kind === 'results';
    }

    root.setAttribute('data-tone', step.tone === 'dark' ? 'dark' : 'light');
    /* Which step is on screen, for the rules that cannot be expressed from
       inside the stage — the rail's background, and the stage's own top
       padding on the steps that carry no meter. */
    root.setAttribute('data-step', step.kind);
    /* Every render starts with no dialog open, so the lift cannot stick. */
    root.setAttribute('data-confirm', 'closed');
  }

  function render() {
    var step = STEPS[state.index];
    if (!step) return;

    /* One place, because every route into a step ends here — next, the
       auto-advance, back, and the restore from localStorage. Hooking the
       navigation callers instead would have meant nine sites and one of them
       forgotten. */
    reportStep(step, state.index);

    /* Any deliberate navigation supersedes a queued auto-advance. */
    if (advanceTimer) {
      window.clearTimeout(advanceTimer);
      advanceTimer = null;
    }

    paintChrome(step);

    var html = '';
    if (step.kind === 'intro') html = viewIntro(step);
    else if (step.kind === 'single' || step.kind === 'multi') html = viewQuestion(step);
    else if (step.kind === 'interstitial') html = viewInterstitial(step);
    else if (step.kind === 'email') html = viewEmail(step);
    else if (step.kind === 'phone') html = viewPhone(step);
    else if (step.kind === 'analysing') html = viewAnalysing();
    else if (step.kind === 'results') html = viewResults();

    stage.innerHTML = html;

    var screen = stage.firstElementChild;
    if (screen) screen.setAttribute('data-enter', '');

    /* The panel, not the window. In a sheet she has not gone anywhere — the
       product page is still behind her, at whatever point she was reading —
       and scrolling it to the top on every step throws that away, most
       visibly on the first step, where opening the quiz appeared to fling the
       page upward for no reason. */
    if (SHEET_MODE) {
      var scroller = root.closest ? root.closest('[data-hq-sheet-body]') : null;
      if (scroller) scroller.scrollTop = 0;
    } else {
      window.scrollTo(0, 0);
    }
    syncRail();

    if (step.kind === 'analysing') runAnalysing();
    /* Checked on every render rather than hooked to one transition: the answer
       set is the condition, not the screen. requestSummary is idempotent, so
       this fires exactly once — the first render after commitment is answered,
       which is the email step. From there the email and phone screens run over
       the top of it, which is the cover the whole design depends on. */
    maybeStartAnalysis();

    if (step.kind === 'results') trackEvent('quiz_completed', {});

    save();
  }

  function viewIntro(step) {
    /* The headline is the number, and nothing else.

       It states the trade because nobody arrives here for a diagnostic — they
       clicked a flag promising a discount, and the first line should be that
       same promise. Naming the price of it in the same breath ("answer 9
       questions, unlock 17% off") put the cost and the reward in one sentence
       and let the cost go first. The reward is the headline now; what it takes
       sits underneath, where it reads as a condition rather than a demand. */
    var title = fillTokens(get(SETTINGS, 'intro_title', ''), 'Unlock [x]% off');
    var sub = fillTokens(get(SETTINGS, 'intro_sub', ''), 'for the lifetime of your subscription');
    var cta = get(SETTINGS, 'intro_cta', 'Start quiz');

    return (
      '<div class="hq-screen hq-screen--intro">' +
      /* No eyebrow on this quiz. "Hair & scalp assessment" framed the page as a
         diagnostic, which is the gift quiz's pitch; here the offer is the pitch
         and the label was one more thing above it competing for the first look. */
      '<h1 class="hq-h hq-h--lg">' + esc(title) + '</h1>' +
      '<p class="hq-sub">' + esc(sub) + '</p>' +
      '<div class="hq-actions">' +
      '<button type="button" class="hq-btn" data-hq-next>' + esc(cta) + '</button>' +
      /* A way out that is not the browser's back button. They arrived from a
         product page mid-decision, and a quiz with no exit reads as a trap —
         which costs more in trust than the handful of starts it saves. */
      '<button type="button" class="hq-skip" data-hq-cancel>cancel</button>' +
      '</div>' +
      '</div>'
    );
  }

  function viewQuestion(step) {
    var selected = state.answers[step.id] || [];
    var multi = step.kind === 'multi';

    var opts = '';
    for (var i = 0; i < step.options.length; i++) {
      var o = step.options[i];
      var on = selected.indexOf(o.id) !== -1;
      opts +=
        '<button type="button" class="hq-opt" ' +
        (multi ? 'aria-pressed="' + (on ? 'true' : 'false') + '"' : 'role="radio" aria-checked="' + (on ? 'true' : 'false') + '"') +
        ' data-hq-opt="' + esc(o.id) + '">' +
        '<span class="hq-opt__check">' + CHECK + '</span>' +
        '<span>' + esc(o.label) + '</span></button>';
    }

    var canContinue = selected.length > 0;

    /* `aria-disabled` rather than `disabled`: a disabled button cannot be
       clicked, so it can never explain itself — the shopper taps, nothing
       happens, and there is no way to learn why. This stays focusable and
       clickable; the handler refuses the step and surfaces the hint. It also
       removes a real cross-browser hazard, since some WebKit versions still
       let events from inside a disabled control reach a delegated listener. */
    return (
      /* Marked so the CTA can pin to the viewport here and only here. Question
         screens are the ones that outgrow a small phone; intro, email and
         results are short enough to lay out normally. */
      '<div class="hq-screen hq-screen--question">' +
      '<h1 class="hq-h">' + esc(step.title) + '</h1>' +
      '<p class="hq-sub">' + esc(step.sub || '') + '</p>' +
      '<div class="hq-options' + (step.grid ? ' hq-options--grid' : '') + '"' +
      (multi ? '' : ' role="radiogroup"') + ' data-hq-options>' + opts + '</div>' +
      '<p class="hq-hint" data-hq-hint role="status">' + esc(hintFor(step)) + '</p>' +
      '<div class="hq-actions">' +
      '<button type="button" class="hq-btn" data-hq-next aria-disabled="' +
      (canContinue ? 'false' : 'true') + '">' + btnLabel('Continue', step.id) + '</button>' +
      '</div></div>'
    );
  }

  /**
   * What this step pays, on the button that completes it.
   *
   * The counter in the rail shows the running total but not the increment, so
   * the connection between answering and earning was left to inference. On the
   * button it is unmissable and it is attached to the action that causes it.
   *
   * Nothing on steps that do not earn — the conditional questions and the
   * interstitials. A "+1%" on a step that pays nothing would be a small lie
   * repeated several times, and the total would visibly fail to move.
   */
  function earnAmount(stepId) {
    if (stepId === 'email') return EMAIL_BONUS;
    if (stepId === 'phone') return PHONE_BONUS;
    if (EARNING[stepId]) return PER_QUESTION;
    return 0;
  }

  /** Earning questions already answered, optionally ignoring one. */
  function answeredEarning(exceptId) {
    var n = 0;
    for (var id in EARNING) {
      if (!EARNING.hasOwnProperty(id)) continue;
      if (exceptId && id === exceptId) continue;
      var a = state.answers[id];
      if (a !== undefined && a !== null && a !== '' && !(a.length === 0)) n++;
    }
    return n;
  }

  function poolAfter(n) {
    return Math.min(QUESTION_POOL, n * PER_QUESTION);
  }

  /**
   * What the badge promises, in whole points — and it has to be exactly what
   * the tally will do.
   *
   * PER_QUESTION is 2.888, so rounding it for display gives every question a
   * "+3%" badge while the total advances by 3, 3, 3, 3, 2, 3... Taking the
   * difference between the rounded totals either side of this answer instead
   * means the badge always names the step the counter is about to take.
   */
  function stepGain(stepId) {
    if (!earnAmount(stepId)) return 0;
    if (stepId === 'phone') return Math.round(PHONE_BONUS);
    var before = answeredEarning(stepId);
    if (stepId === 'email') return Math.round(TARGET) - Math.round(poolAfter(before));
    return Math.round(poolAfter(before + 1)) - Math.round(poolAfter(before));
  }

  /**
   * The label and the reward, centred together as one phrase.
   *
   * No spacer opposite the badge. One was there to centre the label on its own,
   * which put the visible pair off to the right — the thing being centred has
   * to be the thing you can see.
   */
  function btnLabel(text, stepId) {
    var gain = REWARD_FLIGHT ? stepGain(stepId) : 0;
    if (!gain) return esc(text);
    return (
      '<span class="hq-btn__label">' + esc(text) + '</span>' +
      '<span class="hq-btn__earn">+' + esc(wholePct(gain)) + '%</span>'
    );
  }

  /* Kept calm and specific. No error language: nothing has gone wrong, the
     shopper simply has not answered yet. */
  function hintFor(step) {
    if (step.kind === 'multi') {
      return step.maxPick ? 'Select at least one to continue.' : 'Select all that apply to continue.';
    }
    return 'Choose an option to continue.';
  }

  function isAnswered(step) {
    if (!step || (step.kind !== 'single' && step.kind !== 'multi')) return true;
    var a = state.answers[step.id];
    return !!(a && a.length);
  }

  /* Reveal the hint and give the options one quiet nudge. Called only from a
     real attempt to advance, never on load — an untouched question should not
     look like a failure. */
  function refuse(step) {
    var hint = stage.querySelector('[data-hq-hint]');
    var opts = stage.querySelector('[data-hq-options]');
    if (hint) hint.setAttribute('data-show', 'true');
    if (opts) {
      opts.removeAttribute('data-nudge');
      /* Force a reflow so the animation restarts on a repeated attempt. */
      void opts.offsetWidth;
      opts.setAttribute('data-nudge', 'true');
    }
  }

  function viewInterstitial(step) {
    var body = '';

    if (step.claims) {
      body += '<ul class="hq-claims">';
      for (var i = 0; i < step.claims.length; i++) {
        body += '<li>' + CHECK.replace('viewBox', 'class="hq-claim-icon" viewBox') + '<span>' + esc(step.claims[i]) + '</span></li>';
      }
      body += '</ul>';
    }

    if (step.timeline) {
      body += '<ul class="hq-timeline">';
      for (var t = 0; t < step.timeline.length; t++) {
        var row = step.timeline[t];
        body +=
          '<li><span class="hq-timeline__when">' + esc(row.when) + '</span>' +
          '<span class="hq-timeline__what">' + esc(row.what) + '</span>' +
          '<span class="hq-timeline__why">' + esc(row.why) + '</span></li>';
      }
      body += '</ul>';
    }

    var dark = step.tone === 'dark';

    return (
      /* Marked alongside the question screens so the CTA pins here too. This is
         the same problem in a different costume: content long enough to push
         the button off a small screen, on a step where the button is the only
         thing to do. */
      '<div class="hq-screen hq-screen--interstitial' + (dark ? ' hq-screen--dark' : '') + '">' +
      (step.eyebrow ? '<span class="hq-eyebrow">' + esc(step.eyebrow) + '</span>' : '') +
      '<h1 class="hq-h">' + esc(step.title) + '</h1>' +
      '<p class="hq-sub">' + esc(step.body) + '</p>' +
      (step.body2 ? '<p class="hq-sub">' + esc(step.body2) + '</p>' : '') +
      body +
      '<div class="hq-actions">' +
      '<button type="button" class="hq-btn' + (dark ? ' hq-btn--light' : '') + '" data-hq-next>' + esc(step.cta || 'Continue') + '</button>' +
      '</div></div>'
    );
  }

  function viewEmail(step) {
    return (
      /* Marked with the phone step: both ask for a single field and then get
         out of the way, so their actions centre rather than sitting on the
         left edge the question screens share with the headline. */
      '<div class="hq-screen hq-screen--capture">' +
      '<h1 class="hq-h">' + esc(step.title) + '</h1>' +
      '<p class="hq-sub">' + esc(step.sub) + '</p>' +
      '<div class="hq-field">' +
      '<label class="hq-visually-hidden" for="hq-email">Email address</label>' +
      '<input class="hq-input" id="hq-email" type="email" name="email" inputmode="email" autocomplete="email" ' +
      'placeholder="you@example.com" value="' + esc(state.email) + '" data-hq-email>' +
      '<span class="hq-error" data-hq-email-error hidden></span>' +
      '</div>' +
      /* No skip. The email is what unlocks the discount — the ladder pays its
         remainder here — so an out is an invitation to abandon one step from
         the thing they came for, and the copy would have to lie about what
         skipping costs. */
      '<div class="hq-actions">' +
      '<button type="button" class="hq-btn" data-hq-email-submit>' + btnLabel('Unlock my discount', 'email') + '</button>' +
      '</div>' +
      '<p class="hq-consent">' + esc(get(SETTINGS, 'consent_text',
        'By entering your email you agree to receive marketing emails from Fleur. Unsubscribe any time. Your quiz answers are used to personalise what we send you.')) +
      '</p>' +
      '</div>'
    );
  }

  function viewPhone(step) {
    return (
      '<div class="hq-screen hq-screen--capture">' +
      '<h1 class="hq-h">' + esc(step.title) + '</h1>' +
      '<p class="hq-sub">' + esc(step.sub) + '</p>' +
      '<div class="hq-field">' +
      '<label class="hq-visually-hidden" for="hq-phone">Mobile number</label>' +
      '<input class="hq-input" id="hq-phone" type="tel" name="phone" inputmode="tel" autocomplete="tel" ' +
      'placeholder="(555) 123-4567" value="' + esc(state.phone || '') + '" data-hq-phone>' +
      '<span class="hq-error" data-hq-phone-error hidden></span>' +
      '</div>' +
      '<div class="hq-actions">' +
      '<button type="button" class="hq-btn" data-hq-phone-submit>' +
      /* No figure on the button. The headline above it and the meter over it
         are both already stating the number, and this is the last step before
         the results — so it reads as the act of closing the quiz out rather
         than as one more offer competing with the two above it. */
      btnLabel('Finalize discount', 'phone') + '</button>' +
      /* The skip says what taking it does. It is still a real option — consent
         has to be refusable, and the consent language on this screen is
         worthless if the refusal is not — but declining should be a decision
         rather than the cheapest way off the screen. The separate −5% chip is
         gone with the wording change: the label carries the number now, and
         "Lose my 5% bonus −5%" states the same cost twice. */
      '<button type="button" class="hq-skip" data-hq-phone-skip>' +
      'No thanks, lose ' + esc(wholePct(PHONE_BONUS)) + '%</button>' +
      '</div>' +

      '<div class="hq-confirm" data-hq-confirm hidden>' +
      '<div class="hq-confirm__panel" role="dialog" aria-modal="true" aria-labelledby="hq-confirm-title">' +
      '<p class="hq-confirm__title" id="hq-confirm-title">Sure you want to lose ' +
      esc(wholePct(PHONE_BONUS)) + '%?</p>' +
      '<p class="hq-confirm__body">Your number is worth another ' +
      esc(wholePct(PHONE_BONUS)) + '% off this first order. Without it you keep ' +
      esc(wholePct(TARGET)) + '%.</p>' +
      '<button type="button" class="hq-btn hq-confirm__keep" data-hq-confirm-stay>Keep my ' +
      esc(wholePct(PHONE_BONUS)) + '%</button>' +
      '<button type="button" class="hq-skip" data-hq-confirm-leave>No thanks, continue</button>' +
      '</div>' +
      '</div>' +
      /* Express consent, at the point of capture. A number collected without
         it is a TCPA liability rather than a marketing asset, and "skip" has
         to stay a real option for the same reason. */
      '<p class="hq-consent">' + esc(get(SETTINGS, 'sms_consent_text',
        'By entering your number you agree to receive recurring automated marketing texts from Fleur at the number provided. Consent is not a condition of purchase. Msg & data rates may apply. Reply STOP to opt out.')) +
      '</p>' +
      '</div>'
    );
  }

  function viewAnalysing() {
    return (
      '<div class="hq-screen hq-screen--analysing">' +
      /* Wrapped so the spinner is not a direct child of .hq-screen. The
         entrance rule targets direct children, and a spinner that has to argue
         with it over `transform` is a spinner that stops. The wrapper takes the
         entrance; the ring inside is left alone to rotate. */
      '<div class="hq-spinner-wrap" aria-hidden="true"><span class="hq-spinner"></span></div>' +
      '<h1 class="hq-h">unlocking your discount…</h1>' +
      '<p class="hq-ticker" data-hq-ticker aria-live="polite"></p>' +
      '</div>'
    );
  }

  function runAnalysing() {
    /* Usually already in flight by now — requestSummary fires the moment the
       last real question is answered, several screens back. Calling again here
       is a no-op unless something went wrong earlier, in which case this is the
       last chance to start. */
    requestSummary();

    /* The copy follows what the next screen actually shows. The first line is
       true again: reading the answers is now something that happens here. */
    var lines = [
      'Reading your answers…',
      'Adding up what you have earned…',
      'Applying your discount…',
      /* Was "Checking your gift schedule…" — the schedule is no longer on the
         results screen, and a loading line that narrates work the visitor
         never sees is just a delay wearing a costume. Same reason the routine
         line went.
         These last lines are true now: the analysis is genuinely being written
         while she reads them, and the screen waits for it. */
      'Working out what is driving yours…',
      'Writing up your analysis…'
    ];
    var el = stage.querySelector('[data-hq-ticker]');
    var i = 0;

    var tick = window.setInterval(function () {
      if (!el || !el.isConnected) {
        window.clearInterval(tick);
        return;
      }
      el.textContent = lines[i];
      i++;
      if (i >= lines.length) window.clearInterval(tick);
    }, 520);

    /* This screen used to be a fixed 2.9 second beat because there was nothing
       to wait for — the copy came from a warmed row. The quiz generates live
       now, so it waits for the thing it is claiming to do.

       Two bounds, and both matter. The floor keeps it from flashing past when
       the answer is already in hand, which it usually is: generation starts
       when commitment is answered and the email and phone screens run over the
       top of it, so by the time she arrives here it has had a head start of
       however long those took. The ceiling is the promise that this screen
       always ends — a shopper must never be held by a request that is not
       coming back, and the fallback below is a real answer, not an error. */
    var FLOOR_MS = 2900;
    /* Measured warm, a full analysis takes about 19 seconds. The ceiling was
       20s, which left nothing for jitter — a normal generation would have
       tripped it and shown the fallback, and the fallback is the short copy
       this whole change exists to replace.
       30s is the point where something has genuinely gone wrong rather than
       merely taken a while. In practice almost nobody waits near it: generation
       starts when commitment is answered, so the email and phone screens have
       already spent most of those 19 seconds before this screen appears. */
    var CEILING_MS = 30000;
    var started = Date.now();

    var wait = window.setInterval(function () {
      var settled = summary.status === 'ready' || summary.status === 'failed' ||
        summary.status === 'off';
      var waited = Date.now() - started;
      if (waited < FLOOR_MS) return;
      if (!settled && waited < CEILING_MS) return;

      window.clearInterval(wait);
      if (STEPS[state.index] && STEPS[state.index].kind === 'analysing') {
        state.index = nextIndex(state.index);
        render();
      }
    }, 200);
  }

  /* ---------------------------------------------------------------------- */

  /**
   * The analysis is back on this page, in arm B, as a teaser above the offer.
   *
   * It was removed on the reasoning that the deal here is "answer these, get
   * the discount", so a paragraph about telogen effluvium is something the
   * visitor did not ask for standing between her and what she came to collect.
   * That reasoning holds for the shopper the entry point describes. What it
   * missed is that a thirteen-question form asking about menopause stage and
   * medications sets its own expectation regardless of the button label, and
   * that the shoppers it sets it for are the ones a discount does not move —
   * see the block above summaryPanel for the numbers.
   *
   * So it returns in the shape the original objection demands: a teaser, above
   * the offer, expandable rather than expanded, generated behind the analysing
   * screen so it costs no time, and never blocking the discount. buildDiagnosis
   * is what it falls back to and is unchanged; the scoring functions still
   * decide which gifts the schedule shows.
   */
  /* [x] is the percentage they can unlock, [q] the number of questions that
     earn. Substituted rather than hardcoded so both lines stay editable in the
     theme editor, and so a target that changes per entry cannot leave the copy
     asserting a number the page is not honouring. Matches how the gift banner
     already handles [n] and [a]. */
  function fillTokens(raw, fallback) {
    return String(raw || fallback)
      .replace(/\[x\]/g, wholePct(TOTAL))
      .replace(/\[q\]/g, String(EARNING_COUNT));
  }

  /* Once a number is given the headline claims the combined figure, because
     that is what the phone step just promised them. It is the stated sum, not
     the compounded rate — 17% then 5% off the result is 21.4% off list, not
     22% — so the sub carries the qualifier the headline cannot. */
  function totalPct() {
    return state.phone && PHONE_CODE ? TARGET + PHONE_BONUS : TARGET;
  }

  function resultsHeading() {
    var raw = get(SETTINGS, 'results_title', '') || 'your [x]% discount is unlocked.';
    return String(raw)
      .replace(/\[x\]/g, wholePct(totalPct()))
      .replace(/\[q\]/g, String(EARNING_COUNT));
  }

  /* Says where the two figures come from, and that neither needs doing
     anything about. "with the code below" was left over from when the SMS
     bonus was a code to copy out; it is attached to the cart on the way to
     checkout now, and there has been no code below it for some time. */
  function resultsSub() {
    if (!(state.phone && PHONE_CODE)) return '';
    return wholePct(TARGET) + '% off every delivery, plus ' + wholePct(PHONE_BONUS) +
      '% more on your first order. Both applied at checkout.';
  }

  function viewResults() {
    return (
      '<div class="hq-screen hq-results">' +
      '<h1 class="hq-h hq-h--lg">' + esc(resultsHeading()) + '</h1>' +
      (resultsSub() ? '<p class="hq-results__sub">' + esc(resultsSub()) + '</p>' : '') +
      /* Above the offer, and above it deliberately: the discount is what she
         came for, so it stays on the first screenful, and this sits between
         the headline that announces it and the panel that prices it. */
      summaryPanel() +
      planPanel() +
      '<p class="hq-restart">Answered something wrong? <button type="button" data-hq-restart>Retake the quiz</button></p>' +
      '</div>'
    );
  }

  /**
   * The offer: one plan, at the price they just earned, with what arrives free.
   *
   * Only the plan they entered on. The other two are unlocked and they can find
   * them on the product page; showing all three here turns the end of the quiz
   * into a fresh comparison at the one moment the visitor has stopped
   * deliberating.
   */
  /**
   * The product name, shortened for this row.
   *
   * The catalogue title is "bloom peptide hair+scalp serum", which is the right
   * name on the PDP and one word too long in a row that also has to carry a
   * price and a button. "Peptide" is the word that goes: it is the ingredient
   * story, and the bridge above has just spent a sentence on it.
   *
   * Derived from the real title rather than hardcoded, so renaming the product
   * still flows through here — it only ever removes a word it actually finds.
   */
  function displayTitle(title) {
    return String(title || '')
      .replace(/\bpeptide\b/i, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  function planPanel() {
    var plan = chosenPlan();
    var serum = CATALOG.serum;
    if (!plan || !serum) return '';

    var price = Number(plan.price);
    var was = Number(plan.compare_at);
    /* `months` and the term string it built are gone with the line that showed
       them. Note what went with them: the row no longer states the cadence or
       the recurring price. "Cancel or pause any time" in the terms below is
       now the only thing on the screen saying this repeats. */

    var hasBonus = !!(state.phone && PHONE_CODE);
    var firstOrder = Math.round(price * (1 - PHONE_BONUS / 100));

    /* The gift schedule used to render here — a row per gift, each with a
       thumbnail and a delivery month. It is gone from this screen.

       It was the longest thing on the results page and the least load-bearing:
       a list of images below the button, after a shopper had already read a
       summary and a price. It made the panel sloppy and pushed the actual
       offer further from the top. The gifts are still real, still ship with
       the subscription, and are still merchandised on the PDP — they just are
       not what this screen is for. GIFTS is left populated because the
       recommender still scores against it. */

    return (
      '<section class="hq-offer" data-hq-unlock>' +

      /* No "YOUR DISCOUNT HAS BEEN APPLIED" label above the row any more. The
         headline four inches up already says the discount is unlocked, and the
         price in the row shows it struck through with the percentage beside
         it — so a tracked uppercase line asserting it a third time was the
         clutter, not the confirmation. */

      /* One row: the product, what it costs, and the way in. It was a stacked
         block — a 160px image, then a 2.6rem price, then a full-width button —
         which took most of a phone screen to say three things. The summary
         above it now carries the argument, so the offer does not also need to
         be the whole page; it needs to be unmissable and small. */
      '<div class="hq-offer__row">' +

      '<span class="hq-offer__thumb">' +
      (serum.image ? '<img src="' + esc(serum.image) + '" alt="" loading="lazy" width="160" height="160">' : '') +
      '</span>' +

      '<span class="hq-offer__lines">' +
      '<span class="hq-offer__name">' + esc(displayTitle(serum.title)) + '</span>' +
      /* With the code in hand the figure shown is what the first order actually
         costs — a "22% off" badge sitting next to the undiscounted $48 would be
         a number the checkout disproves. The recurring price moves into the
         term line underneath, since that is the one that repeats. */
      '<span class="hq-offer__price">' +
      '<b>' + money(hasBonus ? firstOrder : price) + '</b>' +
      (was > price ? '<s>' + money(was) + '</s>' : '') +
      '<em>' + trimPct(totalPct()) + '% off</em>' +
      '</span>' +
      '</span>' +

      '<button type="button" class="hq-offer__cta" data-hq-subscribe data-hq-plan="' +
      esc(String(plan.selling_plan_id)) + '" data-hq-variant="' + esc(String(plan.variant_id)) + '">' +
      'Start</button>' +

      '</div>' +

      '<p class="hq-sub-panel__terms">' + esc(get(SETTINGS, 'sub_terms',
        'Cancel or pause any time. Gifts ship with the order shown and are available to active subscribers only.')) +
      '</p></section>'
    );
  }

  /**
   * One compact row per product: an 80px thumbnail, the name and the reason,
   * then the price with its own add control. Modelled on the native app's
   * recommendation list rather than the large square cards this used before --
   * seven products rendered as full-bleed cards made the page enormous and
   * pushed the subscription offer far below the fold.
   *
   * The serum is marked, not enlarged. It stays first and takes the solid
   * button; everything else is quieter.
   */
  function cardHTML(rec) {
    var p = rec.product;
    var img = p.image
      ? '<img src="' + esc(p.image) + '" alt="" loading="lazy" width="160" height="160">'
      : '';

    var price = money(p.price);
    if (p.compare_at && Number(p.compare_at) > Number(p.price)) {
      price = '<s>' + money(p.compare_at) + '</s>' + price;
    }

    var added = state.added[p.variant_id] === true;

    return (
      '<li class="hq-rec' + (rec.hero ? ' hq-rec--lead' : '') + '">' +
      '<div class="hq-rec__media">' + img + '</div>' +
      '<div class="hq-rec__body">' +
      (rec.hero && rec.flag ? '<span class="hq-rec__flag">' + esc(rec.flag) + '</span>' : '') +
      '<h3 class="hq-rec__name">' + esc(p.title) + '</h3>' +
      '<p class="hq-rec__why">' + esc(rec.because) + '</p>' +
      '</div>' +
      '<div class="hq-rec__buy">' +
      '<span class="hq-rec__price">' + price + '</span>' +
      '<button type="button" class="hq-rec__add' + (rec.hero ? ' hq-rec__add--solid' : '') + '" ' +
      'data-hq-add="' + esc(p.variant_id) + '">' + (added ? 'Added' : 'Add') + '</button>' +
      '</div></li>'
    );
  }

  /**
   * The subscription offer, built as a gallery: one primary image with a strip
   * of thumbnails under it. The serum leads because it is the thing being
   * subscribed to; the monthly gifts follow. Selecting a thumbnail promotes it
   * into the primary slot.
   *
   * Each thumbnail carries its own image, name and caption as data attributes,
   * so the swap is a read-and-apply with no shared state to keep in sync.
   */
  /**
   * The subscription offer.
   *
   * This is not a browse, so it is not a gallery. At this point the shopper is
   * looking at a priced routine and asking one question: buy it, or subscribe?
   * The honest answer is strong -- almost every tool we just recommended
   * arrives free, one per delivery -- so the section is built to make exactly
   * that comparison, then show the schedule that proves it.
   *
   * Three parts, in the order the decision is made:
   *   1. the comparison, in money
   *   2. what you are actually subscribing to
   *   3. what turns up, and when
   *
   * Nothing here needs a click to be understood.
   */
  function cartRoot() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  /**
   * A fetch from a realm nothing has patched.
   *
   * Appstle's widget wraps window.fetch and XMLHttpRequest.prototype.send and
   * rewrites selling_plan on anything posted to /cart to whatever its own
   * hidden widget is holding -- which on this page is "one time". Verified
   * against the live shop: the identical POST carrying selling_plan
   * 4593811635 lands as a $48 subscription through an untouched fetch and as a
   * $68 one-time bottle through this document's own. So the subscription the
   * shopper had just answered thirteen questions for was being sold to her as
   * a single bottle at list price, the plan discount vanished with the plan,
   * and 5PERCENTFORPHONE went inapplicable because the line it targets was no
   * longer there -- which is why the drawer quoted no discount at all.
   *
   * A blank same-origin iframe has a fresh realm with its own untouched fetch,
   * and its requests carry this document's cookies. The same device the arm-B
   * buybox already uses (_fleurCleanFetch in assets/global.js) and the
   * second-bottle offer already uses (cleanFetch in assets/pdp-double.js).
   */
  function cleanFetch() {
    try {
      var frame = document.createElement('iframe');
      frame.src = 'about:blank';
      frame.setAttribute('aria-hidden', 'true');
      frame.style.cssText = 'position:absolute;width:0;height:0;border:0;opacity:0';
      document.body.appendChild(frame);
      var f = frame.contentWindow && frame.contentWindow.fetch;
      if (!f) { frame.remove(); return null; }
      return {
        fetch: f.bind(frame.contentWindow),
        done: function () { try { frame.remove(); } catch (e) {} }
      };
    } catch (e) { return null; }
  }

  /**
   * Add without the slide-out taking over.
   *
   * UpCart intercepts window.fetch and decides whether to open from the
   * request URL: `upcart=1` marks the call as UpCart-initiated, and
   * `opens_cart=never` maps to the one initiator its own guard refuses to open
   * for. This is the app's own mechanism, not a workaround -- it is how
   * UpCart's internals suppress the drawer for their background calls.
   *
   * So an individual "Add" lands quietly and only moves the header count. The
   * drawer is then opened deliberately, by openCart(), for the actions where
   * seeing the cart is the point.
   */
  function addToCart(items, onDone) {
    /* Only when a selling plan is riding along, because that is the only thing
       Appstle rewrites. Every one-time add -- the routine's own Add buttons --
       keeps this document's fetch exactly as before, so UpCart still reads
       `opens_cart=never` off the URL and still holds the drawer back. */
    var carriesPlan = false;
    for (var p = 0; p < items.length; p++) {
      if (items[p] && items[p].selling_plan) { carriesPlan = true; break; }
    }
    var clean = carriesPlan ? cleanFetch() : null;
    var send = clean ? clean.fetch : fetch;
    var settle = function () {
      if (clean) { clean.done(); clean = null; }
    };

    send(cartRoot() + 'cart/add.js?upcart=1&opens_cart=never', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart');
        return res.json();
      })
      .then(function (data) {
        settle();
        /* UpCart never saw the request, so its copy of the cart is a line
           behind and the drawer would open without the subscription in it.
           Both it and assets/quiz-earned.js re-read the cart on these. */
        if (carriesPlan) {
          try {
            if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
          } catch (e) {}
          try {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          } catch (e) {}
        }
        /* Tell the funnel. This add went through the clean realm, and
           assets/ab-track.js patches *this* document's fetch — which the
           frame's is not. See the bridge there. */
        try {
          document.dispatchEvent(new CustomEvent('fleur:cart-add', { bubbles: true }));
        } catch (e) {}
        for (var i = 0; i < items.length; i++) state.added[items[i].id] = true;
        watchCartMutations();
  refreshCartCount();

  /**
   * Removing a line in the slide-out has to reach this page too, or a row goes
   * on claiming "Added" for something the shopper just deleted.
   *
   * The fetch wrapper above cannot see those requests. UpCart captures a fetch
   * reference at init (`let r = window.fetch`) and calls that captured
   * reference for its own cart operations. Its embed loads before this
   * deferred script, so what it captured is the native fetch — every removal
   * it makes bypasses the wrapper entirely.
   *
   * It does publish its own DOM events for exactly this, and its bundle
   * deprecates the older window.upcartOnX callbacks in favour of them, so
   * these are the supported surface rather than a hook into internals.
   */
  var UPCART_EVENTS = [
    'aftersell-upcart:public-events:cart-updated',
    'aftersell-upcart:public-events:item-removed',
    'aftersell-upcart:public-events:added-to-cart',
    'aftersell-upcart:public-events:cart-loaded',
    'aftersell-upcart:public-events:cart-closed'
  ];
  for (var ue = 0; ue < UPCART_EVENTS.length; ue++) {
    window.addEventListener(UPCART_EVENTS[ue], refreshCartCount);
  }

  /* Theme-level cart events, for anything that is not UpCart. */
  ['cart:refresh', 'cart:updated', 'cart:change'].forEach(function (evt) {
    document.addEventListener(evt, refreshCartCount);
  });

  /* Backstop: re-read on return to the tab, and while the drawer is open, in
     case an edit lands through a path none of the above covers. */
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) refreshCartCount();
  });

  var drawerPoll = null;
  window.addEventListener('aftersell-upcart:public-events:cart-opened', function () {
    if (drawerPoll) return;
    drawerPoll = window.setInterval(refreshCartCount, 1500);
  });
  window.addEventListener('aftersell-upcart:public-events:cart-closed', function () {
    if (!drawerPoll) return;
    window.clearInterval(drawerPoll);
    drawerPoll = null;
    refreshCartCount();
  });
        if (onDone) onDone(null, data);
      })
      .catch(function (err) {
        settle();
        /* Re-read rather than assume. A failed add may still have changed the
           cart, and a badge that disagrees with the cart is worse than a slow
           one. */
        refreshCartCount();
        if (onDone) onDone(err);
      });
  }

  /**
   * Empty the cart. Used before starting a subscription: the routine the
   * shopper may have just added arrives free with the plan, so leaving it in
   * the cart charges them for things they are about to be given.
   *
   * Carries the same suppression params as the add so the drawer does not
   * flash open mid-sequence.
   */
  function clearCart() {
    /* The empty body is required, not incidental: without it there is no
       Content-Length and Shopify answers 411 Length Required, so the clear
       silently does nothing. */
    return fetch(cartRoot() + 'cart/clear.js?upcart=1&opens_cart=never', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: '{}'
    }).then(function (res) {
      if (!res.ok) throw new Error('clear');
      return res;
    });
  }

  /** Open the storefront's slide-out cart, falling back to the cart page. */
  /**
   * Put the phone bonus on the cart without anyone typing it.
   *
   * /discount/<code> sets the discount on the cart session and redirects;
   * fetching it is enough, because what carries is the cookie, not the page it
   * lands on. It survives cart/clear.js — a discount is held against the
   * session rather than the line items — and it carries into checkout.
   *
   * Always resolves. A discount that fails to attach must not strand the
   * shopper on the results screen with a dead button: the subscription is the
   * sale, the bonus is a sweetener, and the two must not share a failure mode.
   * It is applied twice for that reason — once when the number is given and
   * again on the way to the cart — so one dropped request costs nothing.
   */
  function applyDiscount(code, done) {
    if (!code) return done();
    var settled = false;
    var finish = function () {
      if (settled) return;
      settled = true;
      done();
    };
    try {
      fetch(cartRoot() + 'discount/' + encodeURIComponent(code), {
        credentials: 'same-origin'
      }).then(finish, finish);
    } catch (e) {
      finish();
      return;
    }
    /* Not worth more than a moment on the way to the cart. */
    window.setTimeout(finish, 1200);
  }

  /* Where the shopper goes once the subscription is in the cart. Same-origin
     only, and only ever a path on this shop: it is a theme setting rather than
     a constant, and a setting that can be edited into an absolute URL is a way
     to send someone off-site from a page that just took their email. */
  function returnUrl() {
    var raw = String(DISCOUNT.return_url || '');
    if (!/^\/[^/\\]/.test(raw)) return cartRoot() + 'checkout';
    return cartRoot().replace(/\/$/, '') + raw;
  }

  function openCart() {
    if (typeof window.upcartOpenCart === 'function') {
      window.upcartOpenCart();
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    window.location.href = cartRoot() + 'cart';
  }

  /* Whether the current step is the last one. The cart control depends on the
     step as well as the count, and the two are painted from different places —
     the count from a fetch, the step from a render — so the decision lives in
     one function both call rather than being duplicated at each site. */
  var onFinalStep = false;

  /* Visible only on the results screen, and only with something in it.
     A shopper mid-quiz may already have a cart from before they started; the
     icon then sat in the chrome for the whole run, offering an exit from a
     flow whose entire value is delivered at the end. There is nothing to
     collect until the discount is unlocked, so there is nothing to link to. */
  function syncCartButton() {
    var btn = root.querySelector('[data-hq-cart]');
    if (!btn) return;
    btn.hidden = !(onFinalStep && cartCount > 0);
  }

  function paintCartCount(count) {
    var btn = root.querySelector('[data-hq-cart]');
    var num = root.querySelector('[data-hq-cart-count]');
    if (!btn || !num) return;
    cartCount = count;
    num.textContent = count > 99 ? '99+' : String(count);
    syncCartButton();
    btn.setAttribute('aria-label', 'View your cart, ' + count + ' item' + (count === 1 ? '' : 's'));
    var bundle = stage.querySelector('[data-hq-cart-open]');
    if (bundle) {
      bundle.textContent = count > 0 ? 'View my cart · ' + count : 'View my cart';
    }
  }

  /**
   * The badge has to track the cart, not just this page's adds.
   *
   * A shopper who opens the drawer and removes a line changes the cart without
   * the quiz knowing, and the count then sits there wrong — which is exactly
   * the "says 4, cart is empty" case. Every cart mutation goes through
   * /cart/{add,change,update,clear}.js whoever makes it, so watching fetch
   * catches the drawer's edits as well as our own.
   *
   * The native fetch is called through unchanged and the original promise is
   * returned, so this composes with the theme's and UpCart's own patches
   * regardless of which order they load in.
   */
  function watchCartMutations() {
    if (!window.fetch || window.__hqCartWatch) return;
    window.__hqCartWatch = true;

    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (e) {}

      var out = nativeFetch.apply(this, arguments);

      if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i.test(url)) {
        out
          .then(function (res) {
            if (res && res.ok) refreshCartCount();
          })
          .catch(function () {});
      }
      return out;
    };
  }

  /**
   * Re-reads the cart and reconciles everything that depends on it: the badge,
   * which rows show "Added", and what "add all" still needs to add.
   *
   * `state.added` used to be a local belief, set when this page made an add and
   * never corrected. Remove a line in the drawer and it drifts: the row keeps
   * saying "Added" for something no longer in the cart, and "add all" skips it,
   * so the shopper ends up with an incomplete routine. The cart is the only
   * thing that actually knows, so it is what we read.
   */
  function refreshCartCount() {
    return fetch(cartRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart) return null;

        var inCart = {};
        var lines = cart.items || [];
        for (var i = 0; i < lines.length; i++) {
          if (lines[i] && lines[i].variant_id) inCart[lines[i].variant_id] = true;
        }
        state.added = inCart;
        syncAddedButtons();

        if (typeof cart.item_count === 'number') paintCartCount(cart.item_count);
        return cart;
      })
      .catch(function () { return null; });
  }

  /* Repaint the row controls from state.added, so a line removed in the drawer
     stops claiming to be added. */
  function syncAddedButtons() {
    var btns = stage.querySelectorAll('[data-hq-add]');
    for (var i = 0; i < btns.length; i++) {
      if (btns[i].disabled) continue;
      var vid = Number(btns[i].getAttribute('data-hq-add'));
      btns[i].textContent = state.added[vid] ? 'Added' : 'Add';
    }
  }

  function trackEvent(name, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      var evt = { event: name };
      for (var k in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, k)) evt[k] = payload[k];
      }
      window.dataLayer.push(evt);
    } catch (e) {}
  }

  /* =========================================================================
     Events
     ========================================================================= */

  /**
   * Repaint the current question's selection state IN PLACE.
   *
   * Selecting an option must never go through render(): that replaces
   * stage.innerHTML, which restarts the entrance animation on every element
   * and scrolls back to the top. On a long option list the result reads as
   * the page reloading under you on every tap.
   *
   * Only two things actually change when a selection changes — the pressed
   * state of the options and whether Continue is enabled — so only those are
   * touched here.
   */
  function syncSelection(step) {
    var selected = state.answers[step.id] || [];
    var attr = step.kind === 'multi' ? 'aria-pressed' : 'aria-checked';
    var btns = stage.querySelectorAll('[data-hq-opt]');

    for (var i = 0; i < btns.length; i++) {
      var on = selected.indexOf(btns[i].getAttribute('data-hq-opt')) !== -1;
      btns[i].setAttribute(attr, on ? 'true' : 'false');
    }

    var ok = selected.length > 0;

    var cont = stage.querySelector('[data-hq-next]');
    if (cont) {
      /* Read before writing: the bump should fire on the transition into a
         valid answer, not on every tap. On a multi-select, picking a second and
         third option keeps `ok` true, and re-bumping each time would turn a
         reward into a twitch. */
      var wasBlocked = cont.getAttribute('aria-disabled') === 'true';
      cont.setAttribute('aria-disabled', ok ? 'false' : 'true');
      if (ok && wasBlocked) bumpEarn(cont);
    }

    /* The hint retracts the moment it stops being true. */
    var hint = stage.querySelector('[data-hq-hint]');
    if (hint && ok) hint.setAttribute('data-show', 'false');
  }

  /**
   * Draw the eye to the reward at the moment it becomes collectable.
   *
   * It used to animate in on render, but the button sits disabled and dimmed
   * until a question is answered — so the one moment the badge moved was the
   * moment nobody was looking at it. Now it is simply present from the start
   * and reacts when the answer lands, which is both when the button lights up
   * and when the reward becomes real.
   *
   * Class removed, layout forced, class re-added: without reading offsetWidth
   * the browser coalesces both mutations and the animation never restarts, so
   * only the first question of a run would bump.
   */
  function bumpEarn(btn) {
    var badge = btn.querySelector('.hq-btn__earn');
    if (!badge) return;
    badge.classList.remove('is-bumped');
    void badge.offsetWidth;
    badge.classList.add('is-bumped');
  }

  function toggleAnswer(step, optId) {
    var current = state.answers[step.id] || [];
    var opt = null;
    for (var i = 0; i < step.options.length; i++) {
      if (step.options[i].id === optId) opt = step.options[i];
    }

    if (step.kind === 'single') {
      state.answers[step.id] = [optId];
      save();
      syncSelection(step);

      /**
       * Single-select used to advance on its own while multi-select waited for
       * Continue. That is a distinction the shopper cannot see coming: they
       * learn "press Continue" on the first question, then the next one moves
       * under them. Every question now waits for Continue, so the flow behaves
       * the same way throughout.
       *
       * Auto-advance is kept behind a setting rather than deleted — it is a
       * reasonable thing to A/B, since it does remove a tap per screen.
       */
      if (SETTINGS.auto_advance !== true) return;

      /* Re-tapping (or switching answers) restarts the delay rather than
         queueing a second advance. */
      if (advanceTimer) window.clearTimeout(advanceTimer);
      advanceTimer = window.setTimeout(function () {
        advanceTimer = null;
        if (STEPS[state.index] === step) {
          state.index = nextIndex(state.index);
          render();
        }
      }, 260);
      return;
    }

    var idx = current.indexOf(optId);
    var next;

    if (idx !== -1) {
      next = current.slice();
      next.splice(idx, 1);
    } else if (opt && opt.exclusive) {
      next = [optId];
    } else {
      next = [];
      /* Selecting anything real clears a previously ticked "none". */
      for (var c = 0; c < current.length; c++) {
        var wasExclusive = false;
        for (var o = 0; o < step.options.length; o++) {
          if (step.options[o].id === current[c] && step.options[o].exclusive) wasExclusive = true;
        }
        if (!wasExclusive) next.push(current[c]);
      }
      if (step.maxPick && next.length >= step.maxPick) next.shift();
      next.push(optId);
    }

    state.answers[step.id] = next;
    save();
    syncSelection(step);
  }

  root.addEventListener('click', function (evt) {
    var step = STEPS[state.index];
    var target = evt.target;

    var optBtn = target.closest ? target.closest('[data-hq-opt]') : null;
    if (optBtn && step && step.options) {
      toggleAnswer(step, optBtn.getAttribute('data-hq-opt'));
      return;
    }

    if (target.closest && target.closest('[data-hq-next]')) {
      /* Questions are required. This is the gate that actually holds — the
         button's aria-disabled is presentation, this is enforcement. */
      if (!isAnswered(step)) {
        refuse(step);
        return;
      }
      if (step && step.kind === 'email') {
        trackEvent('quiz_email_skipped', {});
      }
      if (step && step.kind === 'phone') {
        trackEvent('quiz_phone_skipped', {});
      }

      /* Whether this answer moved the number, asked of the number itself.

         It used to also require earnAmount(step.id) > 0 — that the step was
         one of the nine named in discount_earning_steps. But the meter counts
         all twelve questions she is asked, so on the other three the price was
         computed higher and never painted: shownPct only advances here. Three
         questions in twelve moved nothing at all, and no amount of rounding
         was ever going to fix that. */
      var awards = earnedPercent() > shownPct;
      var origin = awards
        ? badgeOrigin(target.closest('[data-hq-next]').querySelector('.hq-btn__earn'))
        : null;

      var advance = function () {
        state.index = nextIndex(state.index);
        render();
      };

      if (!awards) { advance(); return; }

      /* Paid on the screen she earned it on, and the next question waits.

         The render used to come first, so the price began falling at the exact
         moment a new question painted over it — two things asking for the same
         eye, and the one she was told to watch for lost. She gets the count to
         herself, then the quiz moves. */
      collect(step, origin);
      window.setTimeout(advance, 900);
      return;
    }

    if (target.closest && target.closest('[data-hq-back]')) {
      state.index = prevIndex(state.index);
      /* Stepping back is a correction, not an award. The tally snaps to the
         truth rather than flying anything, and it can go down. */
      shownPct = earnedPercent();
      render();
      return;
    }

    /* Clicking the backdrop is a dismissal, not a decision — but only when the
       backdrop itself was hit, or every click inside the panel would close it. */
    if (target.matches && target.matches('[data-hq-confirm]')) {
      target.hidden = true;
      root.setAttribute('data-confirm', 'closed');
      return;
    }

    if (target.closest && target.closest('[data-hq-cancel]')) {
      /* Back to where they came from, which is the product they were looking
         at. The referrer is checked for origin before it is followed — it is
         attacker-controllable in general, and sending someone off-site from a
         cancel button would be an open redirect. History is the fallback, and
         the storefront root the last resort for anyone who opened the quiz
         directly and has no history to go back to. */
      var back = null;
      try {
        if (document.referrer &&
            new URL(document.referrer).origin === window.location.origin &&
            new URL(document.referrer).pathname !== window.location.pathname) {
          back = document.referrer;
        }
      } catch (e) {}
      trackEvent('quiz_cancelled', {});
      if (back) window.location.href = back;
      else if (window.history.length > 1) window.history.back();
      else window.location.href = cartRoot();
      return;
    }

    if (target.closest && target.closest('[data-hq-phone-skip]')) {
      var sheet = stage.querySelector('[data-hq-confirm]');
      if (!sheet) {
        state.index = nextIndex(state.index);
        render();
        return;
      }
      sheet.hidden = false;
      root.setAttribute('data-confirm', 'open');
      var keep = sheet.querySelector('[data-hq-confirm-stay]');
      if (keep) keep.focus();
      trackEvent('quiz_phone_skip_prompted', { at_risk: PHONE_BONUS });
      return;
    }

    if (target.closest && target.closest('[data-hq-confirm-stay]')) {
      var sheetKeep = stage.querySelector('[data-hq-confirm]');
      if (sheetKeep) sheetKeep.hidden = true;
      root.setAttribute('data-confirm', 'closed');
      var phoneInput = stage.querySelector('[data-hq-phone]');
      if (phoneInput) phoneInput.focus();
      return;
    }

    if (target.closest && target.closest('[data-hq-confirm-leave]')) {
      trackEvent('quiz_phone_declined', { forfeited: PHONE_BONUS });
      state.index = nextIndex(state.index);
      render();
      return;
    }

    if (target.closest && target.closest('[data-hq-phone-submit]')) {
      submitPhone();
      return;
    }

    if (target.closest && target.closest('[data-hq-email-submit]')) {
      submitEmail();
      return;
    }

    var moreBtn = target.closest ? target.closest('[data-hq-more]') : null;
    if (moreBtn) {
      var moreBody = stage.querySelector('[data-hq-more-body]');
      if (moreBody) {
        var opening = moreBody.hasAttribute('hidden');
        if (opening) moreBody.removeAttribute('hidden');
        else moreBody.setAttribute('hidden', '');
        moreBtn.setAttribute('aria-expanded', opening ? 'true' : 'false');
        moreBtn.textContent = opening ? 'Show less' : 'Read the full analysis';
      }
      return;
    }

    /* A follow-up question, tapped.
       The answer is appended to the thread rather than replacing anything: she
       may tap two questions, and the second answer arriving on top of the first
       would take away something she had just been given. Each answer brings its
       own next three, so it continues as far as she wants it to. */
    var askBtn = target.closest ? target.closest('[data-hq-ask]') : null;
    if (askBtn) {
      var qText = askBtn.getAttribute('data-hq-q') || '';
      var qSig = askBtn.getAttribute('data-hq-ask') || '';
      var thread = stage.querySelector('[data-hq-thread]');
      if (!thread || askBtn.disabled) return;

      askBtn.disabled = true;
      askBtn.classList.add('is-asked');

      var slot = document.createElement('div');
      slot.className = 'hq-analysis__answer';
      slot.innerHTML = '<p class="hq-analysis__asked">' + esc(qText) + '</p>' +
        '<p class="hq-analysis__thinking">Working that out…</p>';
      thread.appendChild(slot);
      if (slot.scrollIntoView) slot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

      var persona = null;
      try { persona = buildSignals().persona || null; } catch (e) {}

      window.fetch(ASK_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: qText,
          sig: qSig,
          persona: persona,
          answers: state.answers || {}
        })
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          if (!d || !d.answer) {
            /* No apology and no retry button. She has the analysis; a
               follow-up that fails should cost her the follow-up and nothing
               else, and the question stays visible above so she can see what
               went unanswered. */
            slot.querySelector('.hq-analysis__thinking').remove();
            askBtn.disabled = false;
            askBtn.classList.remove('is-asked');
            slot.remove();
            return;
          }
          slot.innerHTML =
            '<p class="hq-analysis__asked">' + esc(qText) + '</p>' +
            '<p class="hq-analysis__answer-body">' + esc(d.answer) + '</p>' +
            questionsHtml(d.questions, 'And after that');
        })
        ['catch'](function () {
          askBtn.disabled = false;
          askBtn.classList.remove('is-asked');
          slot.remove();
        });
      return;
    }

    var analysisBtn = target.closest ? target.closest('[data-hq-analysis]') : null;
    if (analysisBtn) {
      var analysisBody = stage.querySelector('[data-hq-analysis-body]');
      if (analysisBody) {
        var showing = analysisBody.hasAttribute('hidden');
        if (showing) analysisBody.removeAttribute('hidden');
        else analysisBody.setAttribute('hidden', '');
        analysisBtn.setAttribute('aria-expanded', showing ? 'true' : 'false');
        analysisBtn.textContent = showing ? 'Hide analysis' : 'Read full analysis';
      }
      return;
    }

    if (target.closest && target.closest('[data-hq-restart]')) {
      state.answers = {};
      state.email = '';
      state.added = {};
      state.index = 0;
      clearSaved();
      render();
      return;
    }

    var addBtn = target.closest ? target.closest('[data-hq-add]') : null;
    if (addBtn) {
      var vid = addBtn.getAttribute('data-hq-add');
      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';
      addToCart([{ id: Number(vid), quantity: 1 }], function (err) {
        addBtn.disabled = false;
        addBtn.textContent = err ? 'Retry' : 'Added';
        if (!err) trackEvent('quiz_add_to_cart', { variant_id: vid });
      });
      return;
    }

    if (target.closest && target.closest('[data-hq-add-all]')) {
      var allBtn = target.closest('[data-hq-add-all]');
      var allLabel = allBtn.innerHTML;
      var recs = scoreProducts(buildSignals());
      allBtn.disabled = true;
      allBtn.textContent = 'Adding…';

      /* Read the cart first, then add only what is genuinely missing. Deciding
         from local state risks both duplicates and, after a removal made in
         the drawer, an incomplete routine. */
      refreshCartCount().then(function () {
        var items = [];
        for (var i = 0; i < recs.length; i++) {
          var vid = Number(recs[i].product.variant_id);
          if (state.added[vid]) continue;
          items.push({ id: vid, quantity: 1 });
        }

        /* Everything is already there — just show them. */
        if (!items.length) {
          allBtn.disabled = false;
          allBtn.innerHTML = allLabel;
          openCart();
          return;
        }

          addToCart(items, function (err) {
          allBtn.disabled = false;
          allBtn.innerHTML = allLabel;
          if (err) {
            allBtn.textContent = 'Try again';
            return;
          }
          trackEvent('quiz_add_routine', { count: items.length });
          openCart();
        });
      });
      return;
    }

    if (target.closest && (target.closest('[data-hq-cart]') || target.closest('[data-hq-cart-open]'))) {
      trackEvent('quiz_view_cart', { count: cartCount });
      openCart();
      return;
    }

    if (target.closest && target.closest('[data-hq-subscribe]')) {
      var subBtn = target.closest('[data-hq-subscribe]');
      var serum = CATALOG.serum;

      /* The plan the button carries, not the catalog's. CATALOG.serum is always
         the single-bottle variant, so subscribing from it would put a visitor
         who entered on "unlock 31%" onto a 1-month plan at 17% — the wrong term
         and the wrong price, silently. The button knows which plan the page is
         selling; trust it, and fall back to the catalog only if it does not. */
      var planId = Number(subBtn.getAttribute('data-hq-plan')) || (serum && Number(serum.selling_plan_id));
      var variantId = Number(subBtn.getAttribute('data-hq-variant')) || (serum && Number(serum.variant_id));
      if (!planId || !variantId) return;
      subBtn.disabled = true;
      subBtn.textContent = 'Starting…';

      /**
       * Build the same line-items the PDP builds.
       *
       * global.js adds a subscription as: month-one gifts FIRST, each carrying
       * `_free_product: 'yes'`, then the plan line carrying
       * `shipping_free: 'yes'`, with one shared `_time_stamp` across all of
       * them. Those properties are what the downstream gift and shipping logic
       * keys off, so a subscription started here has to look identical to one
       * started on the product page or it is treated as a different thing.
       *
       * The underscore prefixes are load-bearing: Shopify hides `_`-prefixed
       * line properties from the cart, which is why `_time_stamp` and
       * `_free_product` have one and `shipping_free` does not.
       */
      var timeStamp = Math.round(new Date().getTime() / 1000);
      var subItems = [];

      for (var mg = 0; mg < MONTH1_GIFTS.length; mg++) {
        if (!MONTH1_GIFTS[mg] || !MONTH1_GIFTS[mg].variant_id) continue;
        subItems.push({
          id: Number(MONTH1_GIFTS[mg].variant_id),
          quantity: 1,
          properties: { '_time_stamp': timeStamp, '_free_product': 'yes' }
        });
      }

      subItems.push({
        id: variantId,
        quantity: 1,
        selling_plan: planId,
        properties: { '_time_stamp': timeStamp, 'shipping_free': 'yes' }
      });

      /* Clear, then add. A failed clear must not block the subscription — the
         worst case is a cart with redundant items, which is recoverable; a
         blocked subscribe is a lost sale. */
      clearCart()
        .catch(function () {})
        .then(function () {
          state.added = {};
          syncAddedButtons();
          addToCart(
            subItems,
            function (err) {
              subBtn.disabled = false;
              /* Matches the button's own label — the panel is one compact row
                 now, and restoring the old full-width wording after a failed add
                 would leave 'Start my subscription' in a pill sized for 'Start'. */
              subBtn.textContent = 'Start';
              if (err) {
                subBtn.textContent = 'Try again';
                return;
              }
              trackEvent('quiz_subscribe', {});
              /* Back to the product page, not the checkout.
                 This went to the checkout for a while, because the SMS bonus is
                 a cart-level discount and the checkout was the only surface
                 that rendered one — the drawer showed the plan price without
                 it, which read as the bonus not having worked. That was a real
                 problem solved the wrong way round: it fixed the number by
                 skipping every step between the quiz and paying.

                 assets/quiz-earned.js now renders the bonus on the product page
                 and in the drawer, read from the cart itself, so the number is
                 visible without having to send anyone to the checkout to see
                 it. They land back where they left off, with the earned prices
                 and the option to move up to a longer plan — which is the one
                 place trading up is still a live decision.

                 After applyDiscount, never before: the code has to be on the
                 session before the page it lands on asks the cart what it is
                 holding. */
              applyDiscount(state.phone ? PHONE_CODE : '', function () {
                /* In a sheet she is already on the page this would send her
                   to, and the subscription is already in her cart. Reloading
                   it would spend the one moment she has just said yes in on a
                   navigation to where she is standing. Whoever hosts the sheet
                   gets to claim that moment by cancelling this; if nobody
                   does, the redirect happens exactly as it always did. */
                if (SHEET_MODE) {
                  var handoff;
                  try {
                    handoff = new CustomEvent('hq:subscribed', {
                      bubbles: true,
                      cancelable: true,
                      detail: { phone: !!state.phone, plan: chosenPlan() }
                    });
                  } catch (e) { handoff = null; }
                  if (handoff && !document.dispatchEvent(handoff)) return;
                }
                window.location.href = returnUrl();
              });
            }
          );
        });
      return;
    }
  });

  /* Deliberately permissive: 10+ digits after stripping punctuation. Strict
     client-side phone validation rejects real numbers — extensions, country
     codes, spacing conventions — and every rejection here is a lost opt-in on
     the highest-friction field in the quiz. */
  function submitPhone() {
    var input = stage.querySelector('[data-hq-phone]');
    var error = stage.querySelector('[data-hq-phone-error]');
    if (!input) return;

    var value = String(input.value || '').replace(/^\s+|\s+$/g, '');
    var digits = value.replace(/\D/g, '');

    if (digits.length < 10) {
      input.setAttribute('aria-invalid', 'true');
      if (error) {
        error.hidden = false;
        error.textContent = 'Please enter a mobile number including area code.';
      }
      input.focus();
      return;
    }

    input.removeAttribute('aria-invalid');
    if (error) error.hidden = true;

    state.phone = value;
    state.phoneBonus = PHONE_BONUS;
    save();

    /* Same shape as the email hand-off so both land on one profile rather than
       two. The email is included deliberately: identify merges on it, and
       without it a number captured in this session can create a second,
       phone-only profile that never joins up with the quiz answers. */
    try {
      if (window._learnq && typeof window._learnq.push === 'function') {
        var pprops = { $phone_number: value };
        if (state.email) pprops.$email = state.email;
        pprops.fleur_quiz_phone_bonus = PHONE_BONUS;
        pprops.fleur_quiz_phone_code = PHONE_CODE;
        pprops.fleur_quiz_sms_opt_in_at = new Date().toISOString();
        window._learnq.push(['identify', pprops]);
        window._learnq.push(['track', 'Quiz SMS Opt In', pprops]);
      }
    } catch (e) {}

    save();
    writeBonus();
    trackEvent('quiz_phone_captured', { bonus: PHONE_BONUS });
    /* Re-sent so the record carries the bonus. The upsert makes this idempotent
       — the same visitor, the same target, one row. */
    recordUnlock();
    /* Attach it now as well. The cart does not exist yet, but the discount is
       held against the session rather than the cart, so it is waiting by the
       time one does — and this is the attempt that has time to spare. */
    applyDiscount(PHONE_CODE, function () {});

    var pawards = earnedPercent() > shownPct;
    var porigin = pawards
      ? badgeOrigin(stage.querySelector('[data-hq-phone-submit] .hq-btn__earn'))
      : null;

    state.index = nextIndex(state.index);
    render();
    if (pawards) collect(null, porigin);
  }

  function submitEmail() {
    var input = stage.querySelector('[data-hq-email]');
    var error = stage.querySelector('[data-hq-email-error]');
    if (!input) return;

    var value = String(input.value || '').trim();
    var valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);

    if (!valid) {
      input.setAttribute('aria-invalid', 'true');
      if (error) {
        error.hidden = false;
        error.textContent = 'That doesn’t look like a valid email address.';
      }
      input.focus();
      return;
    }

    input.removeAttribute('aria-invalid');
    if (error) error.hidden = true;

    state.email = value;
    save();
    writeUnlock();
    recordUnlock();

    /* Hand the address and the full answer set to Klaviyo if the on-site
       object is present. It usually is via the theme's Klaviyo integration;
       when it is not, the quiz still completes rather than blocking on it. */
    try {
      if (window._learnq && typeof window._learnq.push === 'function') {
        var props = { $email: value };
        for (var k in state.answers) {
          if (Object.prototype.hasOwnProperty.call(state.answers, k)) {
            props['quiz_' + k] = state.answers[k].join(', ');
          }
        }
        var signals = buildSignals();
        props.quiz_persona = signals.persona;
        props.quiz_completed_at = new Date().toISOString();
        window._learnq.push(['identify', props]);
        window._learnq.push(['track', 'Completed Hair Quiz', props]);
      }
    } catch (e) {}

    trackEvent('quiz_email_captured', {});

    var awards = earnedPercent() > shownPct;
    var origin = awards
      ? badgeOrigin(stage.querySelector('[data-hq-email-submit] .hq-btn__earn'))
      : null;

    state.index = nextIndex(state.index);
    render();
    if (awards) collect(null, origin);
  }

  /* =========================================================================
     Boot
     ========================================================================= */

  /**
   * The fixed rail is transparent at rest and frosts once the page scrolls
   * under it. Driven from JS rather than a permanent background so the
   * question screens -- which never scroll -- keep a clean, uninterrupted
   * ground, while the results page gets a readable header.
   *
   * The flag is only written when it actually changes, so scrolling does not
   * touch the DOM on every frame.
   */
  var railScrolled = null;

  function syncRail() {
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    var next = y > 4;
    if (next === railScrolled) return;
    railScrolled = next;
    root.setAttribute('data-scrolled', next ? 'true' : 'false');
  }

  /* `{ passive: true }` keeps scrolling smooth. Browsers too old to understand
     the options object read it as a truthy `useCapture`, which still fires. */
  window.addEventListener('scroll', syncRail, { passive: true });

  refreshCartCount();

  restore();

  /* Seed from whatever was restored. A reload mid-quiz should show the total
     already earned, not zero and not an animation replaying rewards that were
     collected in a previous session. */
  shownPct = earnedPercent();

  /* ?restart=1 lets ads and the Klaviyo popup always land on a clean start. */
  if (window.location.search.indexOf('restart=1') !== -1) {
    state.answers = {};
    state.index = 0;
    clearSaved();
    /* A deliberate restart is a new run, not a continuation of the last. */
    newQuizAttempt();
  }

  /* The denominator: she reached the quiz. Everything else is a share of this,
     and the gap between this and the first quiz_step is the cost of the intro
     screen — the one number that says how many people arrive and never begin. */
  quizEvent('quiz_view', null, null);

  render();
})();
